'use client';

/**
 * AutoTranslate — the safety net that makes the Thai toggle complete.
 *
 * The precise path: page components call t() with DICT keys (exact,
 * hand-checked translations). Not every page is wired yet, so when TH is
 * active this layer additionally walks the DOM and swaps any remaining
 * exact-match English UI strings (table headers, stat labels, buttons,
 * placeholders, empty states) using:
 *   1. the reverse of the shared DICT (en → th)
 *   2. the CHROME_PHRASES map below (page chrome not yet in DICT)
 *
 * Exact-match only, element-scoped: user data (names, SKUs, notes) can never
 * be mangled, and input values are never touched. EN restores everything
 * because React re-renders the original text on language flip.
 */

import { useEffect, useContext } from 'react';
import { DICT, LangCurrencyContext, LangCurrencyContextType } from '@/contexts/LangCurrencyContext';

/** en → th for UI chrome on pages not yet individually wired. */
const CHROME_PHRASES: Record<string, string> = {
  // Layout skip link (first focusable element on every page)
  'Skip to main content': 'ข้ามไปยังเนื้อหาหลัก',
  'Newsletter email address': 'อีเมลสำหรับรับข่าวสาร',
  // Engineering page (/engineering) — public infra/QA disclosure
  Engineering: 'งานวิศวกรรม',
  'Unit tests': 'การทดสอบยูนิต',
  'HTTP smoke tests': 'การทดสอบความถูกต้องผ่าน HTTP',
  'SEO audit': 'การตรวจสอบ SEO',
  'Accessibility audit': 'การตรวจสอบการเข้าถึง',
  'Performance audit': 'การตรวจสอบประสิทธิภาพ',
  'Translation audit': 'การตรวจสอบคำแปล',
  '13 checks per release across every public page':
    '13 การตรวจสอบในทุกเวอร์ชัน ครอบคลุมทุกหน้าเว็บสาธารณะ',
  '286 checks covering metadata, structured data and crawl files':
    '286 การตรวจสอบ ครอบคลุมเมทาดาทา ข้อมูลโครงสร้าง และไฟล์สำหรับการเก็บข้อมูล',
  '185 checks covering landmarks, labels, contrast and zoom':
    '185 การตรวจสอบ ครอบคลุมแลนด์มาร์ก ป้ายกำกับ คอนทราสต์ และการซูม',
  '42 checks covering response time, payloads and caching':
    '42 การตรวจสอบ ครอบคลุมเวลาตอบสนอง ขนาดข้อมูล และแคช',
  'Every interface string must have a Thai counterpart':
    'ข้อความบนหน้าจอทุกข้อความต้องมีคำแปลภาษาไทยคู่กัน',
  'Database-level isolation': 'การแยกข้อมูลระดับฐานข้อมูล',
  'Every tenant table is protected by row-level security, so data cannot leak between organisations.':
    'ทุกตารางข้อมูลของผู้ใช้ถูกป้องกันด้วยความปลอดภัยระดับแถว ข้อมูลจึงไม่รั่วไหลข้ามองค์กร',
  'Enforcing content policy': 'นโยบายความปลอดภัยเนื้อหาที่บังคับใช้',
  'A strict content security policy, HSTS, frame denial and referrer policy are set on every response.':
    'ทุกการตอบกลับมีนโยบายความปลอดภัยเนื้อหาแบบเข้มงวด HSTS การปิดการฝังเฟรม และนโยบายการอ้างอิง',
  'Role-based access': 'การควบคุมสิทธิ์ตามบทบาท',
  'Admin, Manager, Sales and Viewer roles are checked server-side, with plan entitlements resolved centrally.':
    'บทบาทผู้ดูแลระบบ ผู้จัดการ ฝ่ายขาย และผู้ชม ถูกตรวจสอบฝั่งเซิร์ฟเวอร์ โดยสิทธิ์ใช้งานแต่ละแพ็กเกจคำนวณจากศูนย์กลาง',
  'Abuse protection': 'ป้องกันการละเมิด',
  'Public forms are rate limited per address and per network, validated, and protected with hidden fields.':
    'แบบฟอร์มสาธารณะจำกัดอัตราการส่งตามที่อยู่และตามเครือข่าย ตรวจสอบข้อมูล และป้องกันด้วยฟิลด์ซ่อน',
  Accountability: 'การตรวจสอบย้อนหลัง',
  'Every user action is recorded with the user, the action, the IP address and the session.':
    'ทุกการกระทำของผู้ใช้ถูกบันทึก พร้อมชื่อผู้ใช้ การกระทำ หมายเลข IP และเซสชัน',
  'Install dependencies and cache them': 'ติดตั้งแพ็กเกจที่จำเป็นและแคชไว้',
  'Lint the whole codebase, zero errors allowed':
    'ตรวจ lint ทั้งโค้ดเบส โดยไม่อนุญาตให้มีข้อผิดพลาดแม้แต่หนึ่งข้อ',
  'Type-check every TypeScript file': 'ตรวจชนิดข้อมูลทุกไฟล์ TypeScript',
  'Run the unit test suite': 'รันชุดทดสอบยูนิต',
  'Verify Thai and English translation coverage': 'ตรวจสอบความครอบคลุมของคำแปลไทยและอังกฤษ',
  'Build the production application': 'บิลด์แอปพลิเคชันสำหรับใช้งานจริง',
  'Start the production server and run the smoke suite':
    'สตาร์ตเซิร์ฟเวอร์จริงและรันชุดทดสอบความถูกต้อง',
  'Run SEO, accessibility and performance audits against it':
    'รันการตรวจสอบ SEO การเข้าถึง และประสิทธิภาพกับเวอร์ชันนั้น',
  'Health endpoint': 'ปลายทางตรวจสถานะระบบ',
  'A public endpoint reports application and database status in real time.':
    'ปลายทางสาธารณะรายงานสถานะของระบบและฐานข้อมูลแบบเรียลไทม์',
  'Uptime monitoring': 'การเฝ้าระวังเวลาที่ระบบทำงาน',
  'The health endpoint is polled every minute with alerting on sustained failure.':
    'มีการตรวจปลายทางสถานะทุกนาที พร้อมแจ้งเตือนเมื่อเกิดความล้มเหลวต่อเนื่อง',
  Backups: 'การสำรองข้อมูล',
  'Daily point-in-time database backups with documented retention.':
    'สำรองฐานข้อมูลแบบ point-in-time ทุกวัน โดยมีกำหนดระยะเวลาเก็บรักษาที่ชัดเจน',
  Rollback: 'การย้อนกลับ',
  'Every release keeps the previous build ready for one-step restoration.':
    'ทุกเวอร์ชันเก็บบิลด์ก่อนหน้าไว้ พร้อมย้อนคืนได้ในขั้นตอนเดียว',
  'Incident runbook': 'คู่มือการรับมือเหตุการณ์',
  'A documented first-response path from health check to mitigation.':
    'แนวทางการตอบสนองเหตุการณ์ครั้งแรกตั้งแต่การตรวจสถานะจนถึงการแก้ไข',
  'Conforms to WCAG 2.1 Level AA across the whole public site':
    'ผ่านมาตรฐาน WCAG 2.1 ระดับ AA ทั้งเว็บไซต์สาธารณะ',
  'Keyboard bypass link as the first focusable element on every page':
    'ลิงก์ข้ามเนื้อหาด้วยแป้นพิมพ์ เป็นองค์ประกอบแรกที่โฟกัสได้ในทุกหน้า',
  'Visible focus indicators and full reduced-motion support':
    'มีตัวบ่งชี้โฟกัสที่มองเห็นได้ และรองรับการลดการเคลื่อนไหวอย่างเต็มที่',
  'Every form control programmatically labelled': 'ทุกช่องกรอกแบบฟอร์มมีป้ายกำกับในระดับโค้ด',
  'Verified with an automated audit on every release': 'ตรวจสอบด้วยการตรวจอัตโนมัติในทุกเวอร์ชัน',
  'Row-level security per organisation, enforced in the database':
    'ความปลอดภัยระดับแถวสำหรับแต่ละองค์กร บังคับใช้ที่ฐานข้อมูล',
  'Engineering at Glanz CRM': 'งานวิศวกรรมของ Glanz CRM',
  'Technology stack': 'เทคโนโลยีที่ใช้',
  'How the platform is built, secured, tested and operated. Every claim on this page is backed by an automated check that runs on every release.':
    'แพลตฟอร์มนี้ถูกสร้าง รักษาความปลอดภัย ทดสอบ และดูแลอย่างไร ทุกข้อความบนหน้านี้มีการตรวจสอบอัตโนมัติรองรับในทุกเวอร์ชัน',
  'Quality gates': 'เกณฑ์คุณภาพ',
  'Every pull request passes all of these before it can ship.':
    'ทุกคำขอรวมโค้ดต้องผ่านเกณฑ์ทั้งหมดนี้ก่อนจึงจะได้รวมเข้าสู่ระบบ',
  'Security posture': 'มาตรการด้านความปลอดภัย',
  'Full details are published on the': 'รายละเอียดทั้งหมดเผยแพร่บน',
  'security page': 'หน้าความปลอดภัย',
  'Delivery pipeline': 'กระบวนการส่งมอบซอฟต์แวร์',
  'Operations and reliability': 'การดำเนินงานและความเสถียร',
  'Questions about our engineering?': 'มีคำถามเกี่ยวกับงานวิศวกรรมของเรา?',
  'We are happy to walk through the architecture, the security model or a technical evaluation for your team.':
    'เรายินดีอธิบายสถาปัตยกรรม โมเดลความปลอดภัย หรือประเมินด้านเทคนิคสำหรับทีมของคุณ',
  'Talk to us': 'พูดคุยกับเรา',
  // Shared form chrome (contact, lead, demo, signup)
  'Work Email *': 'อีเมลบริษัท *',
  'Company Name': 'ชื่อบริษัท',
  'Confirm Password': 'ยืนยันรหัสผ่าน',
  'Select your role...': 'เลือกบทบาทของคุณ...',
  'Sign in': 'เข้าสู่ระบบ',
  'Creating account...': 'กำลังสร้างบัญชี...',
  'Create Your Demo Account': 'สร้างบัญชีทดลองของคุณ',
  'Create Your Account': 'สร้างบัญชีของคุณ',
  'Activating your account...': 'กำลังเปิดใช้งานบัญชี...',
  'Account Activated!': 'เปิดใช้งานบัญชีแล้ว!',
  'Back to plans': 'กลับไปดูแพ็กเกจ',
  'Check your inbox at': 'ตรวจอีเมลของคุณที่',
  "What's in your email": 'มีอะไรในอีเมลของคุณ',
  'Your account will be activated immediately. Login details sent by email.':
    'บัญชีของคุณจะเปิดใช้งานทันที ข้อมูลเข้าสู่ระบบจะถูกส่งทางอีเมล',
  'Login details will be emailed immediately after signup.':
    'ข้อมูลเข้าสู่ระบบจะถูกส่งทางอีเมลทันทีหลังสมัคร',
  'Login to Your Account →': 'เข้าสู่ระบบบัญชีของคุณ →',
  Accessibility: 'การเข้าถึง',
  // Marketing copy discovered by the JSX-text coverage pass
  'Generating PDF…': 'กำลังสร้าง PDF…',
  "Glanz CRM adapts to how you work — whether you're a solo appraiser, a Bangkok retailer, or a multi-branch wholesaler.":
    'Glanz CRM ปรับตัวให้เข้ากับวิธีทำงานของคุณ — ไม่ว่าคุณจะเป็นผู้ประเมินรายบุคคล ร้านค้าในกรุงเทพฯ หรือผู้ค้าส่งหลายสาขา',
  // Marketing tour / step controls
  'Next step': 'ถัดไป',
  'Previous step': 'ก่อนหน้า',
  'PDF generated ✓': 'สร้าง PDF แล้ว ✓',
  'PDF generation failed:': 'สร้าง PDF ล้มเหลว:',
  'Wholesale (−5%)': 'ขายส่ง (−5%)',
  '3 items · $31,400': '3 รายการ · $31,400',
  '$22,800 · 3 weeks ago': '$22,800 · 3 สัปดาห์ที่แล้ว',
  // Contact page office hours
  'Monday – Friday: 9:00 – 18:00 ICT': 'จันทร์ – ศุกร์: 9:00 – 18:00 น. (ICT)',
  // Features module taglines
  "Create professional appraisal reports with your firm's branding.":
    'สร้างรายงานประเมินราคาอย่างมืออาชีพพร้อมแบรนด์ของคุณเอง',
  "Track how a stone's appraised value has changed over time.":
    'ติดตามการเปลี่ยนแปลงมูลค่าประเมินของอัญมณีตามช่วงเวลา',
  // Signup form password hint
  '8+ characters': 'อย่างน้อย 8 ตัวอักษร',
  // Generic table / label chrome
  Status: 'สถานะ',
  Statuses: 'สถานะ',
  Actions: 'จัดการ',
  Action: 'จัดการ',
  Customer: 'ลูกค้า',
  Customers: 'ลูกค้า',
  Diamond: 'เพชร',
  Diamonds: 'เพชร',
  SKU: 'รหัสสินค้า',
  Shape: 'รูปทรง',
  Carat: 'กะรัต',
  Color: 'สี',
  Clarity: 'ความสะอาด',
  Price: 'ราคา',
  'Price (USD)': 'ราคา (USD)',
  Cost: 'ต้นทุน',
  Value: 'มูลค่า',
  Total: 'รวม',
  Subtotal: 'ยอดรวม',
  Discount: 'ส่วนลด',
  Quantity: 'จำนวน',
  Qty: 'จำนวน',
  Date: 'วันที่',
  Notes: 'หมายเหตุ',
  Note: 'หมายเหตุ',
  Description: 'รายละเอียด',
  Details: 'รายละเอียด',
  Name: 'ชื่อ',
  'Full Name': 'ชื่อ-นามสกุล',
  Email: 'อีเมล',
  'Email Address': 'อีเมล',
  Phone: 'โทรศัพท์',
  'Phone Number': 'เบอร์โทรศัพท์',
  Address: 'ที่อยู่',
  Location: 'สถานที่เก็บ',
  'Location Distribution': 'การกระจายตามสถานที่เก็บ',
  Type: 'ประเภท',
  Method: 'วิธีชำระ',
  Payment: 'การชำระเงิน',
  'Payment Method': 'วิธีชำระเงิน',
  Reference: 'อ้างอิง',
  'Reference / Receipt No.': 'อ้างอิง / เลขที่ใบเสร็จ',
  Category: 'หมวดหมู่',
  Supplier: 'ผู้จัดส่ง',
  Suppliers: 'ผู้จัดส่ง',
  Invoice: 'ใบแจ้งหนี้',
  Invoices: 'ใบแจ้งหนี้',
  'Sale Date': 'วันที่ขาย',
  Sales: 'ยอดขาย',
  Revenue: 'รายรับ',
  Expenses: 'รายจ่าย',
  Profit: 'กำไร',
  Margin: 'อัตรากำไร',
  'Margin %': 'อัตรากำไร %',
  'Gross Margin': 'กำไรขั้นต้น',
  Transactions: 'รายการ',
  Created: 'สร้างเมื่อ',
  Updated: 'อัปเดตเมื่อ',
  'Created At': 'สร้างเมื่อ',
  'Updated At': 'อัปเดตเมื่อ',
  Timestamp: 'เวลา',
  'Start Date': 'วันเริ่มต้น',
  'End Date': 'วันสิ้นสุด',
  'Due Date': 'วันครบกำหนด',
  'Expiry Date': 'วันหมดอายุ',
  Expiry: 'วันหมดอายุ',
  Expires: 'หมดอายุ',
  Expired: 'หมดอายุ',
  'Days Left': 'วันที่เหลือ',
  'Hold Date': 'วันที่จอง',
  'Return Due': 'วันคืนสินค้า',
  'Signup Date': 'วันสมัคร',
  'Sent At': 'ส่งเมื่อ',
  'Paid At': 'ชำระเมื่อ',
  'Issued To': 'ออกให้',
  Recipient: 'ผู้รับ',
  'Performed By': 'ดำเนินการโดย',
  'Target User': 'ผู้ใช้เป้าหมาย',
  Owner: 'เจ้าของ',
  Manager: 'ผู้จัดการ',
  User: 'ผู้ใช้',
  Users: 'ผู้ใช้',
  Username: 'ชื่อผู้ใช้',
  Role: 'บทบาท',
  'Role Assignment': 'มอดหมายบทบาท',
  'Role & Permissions': 'บทบาทและสิทธิ์',
  Department: 'แผนก',
  'Job Title': 'ตำแหน่งงาน',
  'Display Name': 'ชื่อที่แสดง',
  Password: 'รหัสผ่าน',
  'Current Password': 'รหัสผ่านปัจจุบัน',
  'New Password': 'รหัสผ่านใหม่',
  'Passwords do not match': 'รหัสผ่านไม่ตรงกัน',
  Required: 'จำเป็น',
  'Loading…': 'กำลังโหลด…',
  'Loading...': 'กำลังโหลด...',
  'Loading... (list)': 'กำลังโหลด...',
  Loading: 'กำลังโหลด',
  'N/A': 'ไม่ระบุ',
  Active: 'ใช้งาน',
  Inactive: 'ไม่ใช้งาน',
  Pending: 'รอดำเนินการ',
  'Pending Invoices': 'ใบแจ้งหนี้ที่รอชำระ',
  Completed: 'เสร็จสิ้น',
  Cancelled: 'ยกเลิก',
  Verified: 'ตรวจสอบแล้ว',
  Suspended: 'ระงับ',
  Suspend: 'ระงับ',
  Reactivate: 'เปิดใช้งานอีกครั้ง',
  Revoke: 'เพิกถอน',
  Revoked: 'เพิกถอนแล้ว',
  Deleted: 'ลบแล้ว',
  New: 'ใหม่',
  Read: 'อ่านแล้ว',
  Replied: 'ตอบกลับแล้ว',
  Retry: 'ลองใหม่',
  Refresh: 'รีเฟรช',
  Reason: 'เหตุผล',
  Message: 'ข้อความ',
  Subject: 'หัวข้อ',
  Provider: 'ผู้ให้บริการ',
  Schema: 'โครงสร้าง',
  Support: 'ฝ่ายสนับสนุน',
  Default: 'ค่าเริ่มต้น',
  Current: 'ปัจจุบัน',
  'Current Plan': 'แพ็กเกจปัจจุบัน',
  'Most Popular': 'ยอดนิยม',
  Featured: 'แนะนำ',
  Final: 'สุดท้าย',
  'Final confirmation': 'ยืนยันขั้นสุดท้าย',
  Other: 'อื่น ๆ',
  Unlimited: 'ไม่จำกัด',
  Monthly: 'รายเดือน',
  Yearly: 'รายปี',
  Lifetime: 'ตลอดชีพ',
  Trial: 'ทดลองใช้',
  'Trial Days': 'วันทดลองใช้',
  Free: 'ฟรี',
  Essential: 'เอสเซนเชียล',
  Starter: 'สตาร์ทเตอร์',
  Professional: 'โปรเฟสชันแนล',
  Enterprise: 'เอนเทอร์ไพรส์',
  USD: 'USD',
  THB: 'THB',
  MRR: 'รายได้ประจำ (MRR)',
  'MRR Trend': 'แนวโน้มรายได้ประจำ',
  'New vs Churned MRR': 'MRR ใหม่ vs ที่หายไป',
  'Subscriptions by Plan': 'การสมัครตามแพ็กเกจ',
  'Revenue by Payment Method': 'รายรับตามวิธีชำระ',
  'Revenue by Stage': 'รายรับตามขั้นตอน',
  'Total Payments': 'ยอดชำระรวม',
  'Total Offline Revenue': 'รายได้สดรวม',
  'Outstanding Balance': 'ยอดค้างชำระ',
  Usage: 'การใช้งาน',
  'Feature Usage': 'การใช้ฟีเจอร์',
  'Import Usage': 'การนำเข้าข้อมูล',
  'Near quota limit': 'ใกล้ถึงโควตา',
  Plan: 'แพ็กเกจ',
  'Plan Name': 'ชื่อแพ็กเกจ',
  'Plan Price': 'ราคาแพ็กเกจ',
  'Plan Limits': 'ขีดจำกัดแพ็กเกจ',
  'Plan Name (EN)': 'ชื่อแพ็กเกจ',
  'Included in your plan': 'รวมอยู่ในแพ็กเกจของคุณ',
  'Included Features': 'ฟีเจอร์ที่รวมอยู่',
  'Features Included': 'ฟีเจอร์ที่รวมอยู่',
  Features: 'ฟีเจอร์',
  'Key Capabilities': 'ความสามารถหลัก',
  'My Subscription': 'แพ็กเกจของฉัน',
  'Subscription Plans': 'แพ็กเกจสมาชิก',
  'Subscription cancels on': 'การสมัครสิ้นสุดวันที่',
  Upgrading: 'กำลังอัปเกรด',
  'Create your first plan': 'สร้างแพ็กเกจแรก',
  'Next Billing': 'ผู้ชำระครั้งถัดไป',
  'Digest Mode': 'โหมดสรุป',
  'Email Preferences': 'ตั้งค่าอีเมล',
  'Send Email': 'ส่งอีเมล',
  'Maintenance Mode': 'โหมดบำรุงรักษา',
  Timezone: 'เขตเวลา',
  Theme: 'ธีม',
  'Security Event Log': 'บันทึกเหตุการณ์ความปลอดภัย',
  'Legal Documents': 'เอกสารทางกฎหมาย',
  'Privacy & Legal': 'ความเป็นส่วนตัวและกฎหมาย',
  'Privacy Policy': 'นโยบายความเป็นส่วนตัว',
  'Do Not Sell My Personal Information': 'ไม่ยินยอมให้ขายข้อมูลส่วนบุคคล',
  'Programmatic access to your data': 'การเข้าถึงข้อมูลผ่านโปรแกรม',
  'Licence Key': 'รหัสไลเซนส์',
  'Licence Type': 'ประเภทไลเซนส์',
  'License & Demo Information': 'ข้อมูลไลเซนส์และเดโม',
  'Yearly License': 'ไลเซนส์รายปี',
  'Generate Licence Key': 'สร้างรหัสไลเซนส์',
  'General Settings': 'ตั้งค่าทั่วไป',
  'Profile Information': 'ข้อมูลโปรไฟล์',
  'Super Admin': 'ผู้ดูแลระบบสูงสุด',
  'Super Admin Panel': 'แผงผู้ดูแลระบบสูงสุด',
  'User Management': 'จัดการผู้ใช้',
  'Customer Portal': 'พอร์ทัลลูกค้า',
  'Deal Pipeline': 'ไปป์ไลน์การขาย',
  'Pipeline Analytics': 'วิเคราะห์ไปป์ไลน์',
  'Pipeline Stage Distribution': 'การกระจายขั้นตอนไปป์ไลน์',
  Stage: 'ขั้นตอน',
  'Stage Breakdown': 'แยกตามขั้นตอน',
  'Stage Performance Summary': 'สรุปผลงานแต่ละขั้นตอน',
  'Deal Outcomes': 'ผลลัพธ์ของดีล',
  'Deal Title': 'ชื่อดีล',
  Deals: 'ดีล',
  Probability: 'โอกาสสำเร็จ',
  'Close Date': 'วันปิดดีล',
  'Avg Days': 'เฉลี่ยวัน',
  Conversion: 'การเปลี่ยน',
  'Drop deals here': 'ลากดีลมาวางที่นี่',
  'Upsell Opportunities': 'โอกาสขายเพิ่ม',
  'Sales Automation': 'ระบบขายอัตโนมัติ',
  'Inventory Aging Report': 'รายงานอายุสินค้า',
  'Profit & Loss Summary': 'สรุปกำไรขาดทุน',
  'Select at least 2 diamonds to compare': 'เลือกเพชรอย่างน้อย 2 เม็ดเพื่อเปรียบเทียบ',
  'Select Diamonds for Bulk Action': 'เลือกเพชรเพื่อจัดการเป็นชุด',
  'Select a customer to view their portal': 'เลือกลูกค้าเพื่อดูพอร์ทัลของลูกค้า',
  'No users found': 'ไม่พบผู้ใช้',
  'No invoices yet': 'ยังไม่มีใบแจ้งหนี้',
  'No invoices for this customer': 'ไม่มีใบแจ้งหนี้ของลูกค้านี้',
  'No memos for this customer': 'ไม่มีใบเมโมของลูกค้านี้',
  'No holds for this customer': 'ไม่มีการจองของลูกค้านี้',
  'No diamonds match your search': 'ไม่มีเพชรที่ตรงกับการค้นหา',
  'No diamonds match the selected filters': 'ไม่มีเพชรที่ตรงกับตัวกรอง',
  'No audit entries found': 'ไม่พบรายการตรวจสอบ',
  'No active items': 'ไม่มีรายการที่ใช้งาน',
  'No active alerts': 'ไม่มีการแจ้งเตือน',
  'No active users found': 'ไม่พบผู้ใช้ที่ใช้งาน',
  'No contact messages yet': 'ยังไม่มีข้อความติดต่อ',
  'No demo registrations found': 'ไม่พบการลงทะเบียนทดลองใช้',
  'No email logs yet': 'ยังไม่มีบันทึกอีเมล',
  'No licence keys found': 'ไม่พบรหัสไลเซนส์',
  'No organizations found': 'ไม่พบองค์กร',
  'No subscription plans found': 'ไม่พบแพ็กเกจสมาชิก',
  'No features added yet': 'ยังไม่มีฟีเจอร์ที่เพิ่ม',
  'No offline payments recorded yet': 'ยังไม่มีการบันทึกชำระสด',
  'Loading active users...': 'กำลังโหลดผู้ใช้ที่ใช้งาน...',
  'Loading email logs...': 'กำลังโหลดบันทึกอีเมล...',
  'Loading messages...': 'กำลังโหลดข้อความ...',
  'Loading payments...': 'กำลังโหลดการชำระเงิน...',
  'Loading plans...': 'กำลังโหลดแพ็กเกจ...',
  'Loading registrations...': 'กำลังโหลดการลงทะเบียน...',
  'Memo #': 'เลขที่เมโม',
  'Ref #': 'เลขที่อ้างอิง',
  'Invoice / PO': 'ใบแจ้งหนี้ / ใบสั่งซื้อ',
  Spec: 'สเปก',
  Measurements: 'การวัด',
  'Grading Details': 'รายละเอียดการจัดเกรด',
  'Module Reference': 'อ้างอิงโมดูล',
  Driver: 'ตัวขับ',
  Lowest: 'ต่ำสุด',
  'Recent Invoices': 'ใบแจ้งหนี้ล่าสุด',
  Received: 'รับแล้ว',
  Marketing: 'การตลาด',
  Currency: 'สกุลเงิน',
  'Glanz CRM': 'กลันซ์ ซีอาร์เอ็ม',
  'Glanz Diamond CRM': 'กลันซ์ ดิอะมอนด์ ซีอาร์เอ็ม',

  // AI assistant
  'How can I help you today?': 'วันนี้มีอะไรให้ช่วยไหม?',
  "Ask me anything about diamonds, customers, pricing, or sales strategy. I'm here to help you close more deals.":
    'ถามอะไรก็ได้เกี่ยวกับเพชร ลูกค้า ราคา หรือกลยุทธ์การขาย พร้อมช่วยให้คุณปิดดีลได้มากขึ้น',
  'Diamond Recommendation': 'แนะนำเพชร',
  'Lead Qualification': 'คัดกรองลูกค้า',
  'Pricing Strategy': 'กลยุทธ์ราคา',
  'Customer Query': 'คำถามลูกค้า',
  'Inventory Advice': 'คำแนะนำสินค้า',
  'Powered by OpenAI': 'ขับเคลื่อนด้วย OpenAI',

  // About page
  'About Us': 'เกี่ยวกับเรา',
  'Our Mission': 'พันธกิจของเรา',
  'Our Story': 'เรื่องราวของเรา',
  'Our Values': 'ค่านิยมของเรา',
  'Contact Us': 'ติดต่อเรา',
  'Learn More': 'ดูเพิ่มเติม',
  'Get Started': 'เริ่มต้นใช้งาน',
  'Sign In': 'เข้าสู่ระบบ',
  'Sign Up': 'สมัครสมาชิก',
  'Log Out': 'ออกจากระบบ',
  'Back to Site': 'กลับสู่เว็บไซต์',
  'Switch to Site': 'สลับไปที่เว็บไซต์',
  'View Pricing': 'ดูราคา',
  'Try Free Demo': 'ทดลองใช้ฟรี',

  // Common buttons / actions
  Save: 'บันทึก',
  'Save Changes': 'บันทึกการเปลี่ยนแปลง',
  Cancel: 'ยกเลิก',
  Edit: 'แก้ไข',
  Delete: 'ลบ',
  Close: 'ปิด',
  Add: 'เพิ่ม',
  Remove: 'เอาออก',
  Confirm: 'ยืนยัน',
  Search: 'ค้นหา',
  Filter: 'ตัวกรอง',
  Clear: 'ล้าง',
  Apply: 'ใช้',
  Submit: 'ส่ง',
  Update: 'อัปเดต',
  Export: 'ส่งออก',
  Import: 'นำเข้า',
  Back: 'กลับ',
  Next: 'ถัดไป',
  Previous: 'ก่อนหน้า',
  Yes: 'ใช่',
  No: 'ไม่',
  Open: 'เปิด',
  Won: 'สำเร็จ',
  Lost: 'ไม่สำเร็จ',
  Draft: 'ฉบับร่าง',
  Sent: 'ส่งแล้ว',
  Paid: 'ชำระแล้ว',
  Overdue: 'เกินกำหนด',
  Partial: 'ชำระบางส่วน',

  // Advanced inventory management
  'Advanced Inventory Management': 'การจัดการสินค้าขั้นสูง',
  'Diamond grading details, certificate tracking, and stock alerts':
    'รายละเอียดการจัดเกรดเพชร การติดตามใบรับรอง และการแจ้งเตือนสต็อก',
  'Total Diamonds': 'เพชรทั้งหมด',
  'Cert Issues': 'ปัญหาใบรับรอง',
  'Aging Stock (90d+)': 'สต็อกเก่า (90+ วัน)',
  'Active Alerts': 'การแจ้งเตือนที่ใช้งาน',
  Inventory: 'สินค้า',
  'Certificate Tracker': 'ติดตามใบรับรอง',
  'Alerts & Actions': 'การแจ้งเตือนและจัดการ',
  'Bulk Operations': 'จัดการเป็นชุด',
  Valid: 'ใช้ได้',
  'Cert Issue': 'ปัญหาใบรับรอง',
  'Aging Stock': 'สต็อกเก่า',
  'in stock': 'ในสต็อก',
  margin: 'อัตรากำไร',
  Certificate: 'ใบรับรอง',
  Certificates: 'ใบรับรอง',
  Alerts: 'การแจ้งเตือน',

  // Analytics page
  Analytics: 'การวิเคราะห์',
  'Live inventory and customer insights': 'ข้อมูลเชิงลึกสินค้าและลูกค้าแบบสด',
  'Total Inventory Value': 'มูลค่าสินค้ารวม',
  'Total Sales Revenue': 'รายได้จากการขายรวม',
  'Avg Carat Weight': 'น้ำหนักกะรัตเฉลี่ย',
  'Top Customer': 'ลูกค้าอันดับต้น',
  'across all inventory': 'จากสินค้าทั้งหมด',
  total: 'รวม',
  transactions: 'รายการ',
  'Clarity Distribution': 'การกระจายตามความสะอาด',
  'Diamond count by clarity grade': 'จำนวนเพชรแยกตามเกรดความสะอาด',
  'Shape Distribution': 'การกระจายตามรูปทรง',
  'Inventory count by diamond shape': 'จำนวนสินค้าแยกตามรูปทรงเพชร',
  'Top Customers by Purchase Volume': 'ลูกค้าอันดับต้นตามยอดซื้อ',
  'Ranked by total purchases (USD)': 'จัดอันดับตามยอดซื้อรวม (USD)',
  'Diamond count by vault location': 'จำนวนเพชรแยกตามสถานที่เก็บ',

  // Deal pipeline / CRM misc
  'Deal flow from Prospect to Closed Won': 'การไหลของดีลจากผู้สนใจถึงปิดการขาย',
  Demo: 'เดโม',
  Product: 'สินค้า',
  Organization: 'องค์กร',
  'Opt Out': 'ไม่รับข่าวสาร',
  'Opted Out': 'ไม่รับข่าวสารแล้ว',
  'Enable rule immediately': 'เปิดใช้กฎทันที',
  'Extend Demo': 'ขยายเวลาเดโม',
  'Sort Order': 'ลำดับการเรียง',
  'Top Method': 'วิธีหลัก',
  'This browser': 'เบราว์เซอร์นี้',
  'Revenue & Business Reporting': 'รายงานรายได้และธุรกิจ',
  'Select diamond for': 'เลือกเพชรสำหรับ',

  // Login / auth / demo
  'Welcome back': 'ยินดีต้อนรับกลับมา',
  'Forgot password?': 'ลืมรหัสผ่าน?',
  'Remember me': 'จำฉันไว้',
  'Demo Account': 'บัญชีทดลอง',

  // JSX-interleaved leaf nodes (expressions split strings into separate text
  // nodes: `{n}d in stock` renders as "12" + "d in stock").
  'd in stock': ' วันในสต็อก',
  'd left': ' วันที่เหลือ',
  '%margin': '% อัตรากำไร',
  '% margin': '% อัตรากำไร',

  // Remaining page chrome (account-settings, billing, super-admin, pipeline,
  // consignment, subscription, customer-portal, comparison, settings tabs)
  'API Access': 'การเข้าถึง API',
  'API keys are coming soon': 'API keys เร็ว ๆ นี้',
  'About Glanz CRM Platform': 'เกี่ยวกับแพลตฟอร์ม Glanz CRM',
  Account: 'บัญชี',
  'Account Settings': 'ตั้งค่าบัญชี',
  'Account Status': 'สถานะบัญชี',
  'Active Plan': 'แพ็กเกจปัจจุบัน',
  'Active Sessions': 'อุปกรณ์ที่ล็อกอิน',
  'Admin Note': 'โน้ตผู้ดูแล',
  After: 'หลังจาก',
  'Agreed Price': 'ราคาตกลง',
  'All Actions': 'ทุกการดำเนินการ',
  'All Cycles': 'ทุกรอบ',
  'All Methods': 'ทุกวิธี',
  'All Plans': 'ทุกแพ็กเกจ',
  'All Roles': 'ทุกบทบาท',
  'All Status': 'ทุกสถานะ',
  'All accounts are paying full list price.': 'ทุกบัญชีจ่ายราคาเต็มตามรายการ',
  'Available Add-Ons': 'ส่วนเสริมที่พร้อมใช้',
  'Average Days Spent per Stage': 'จำนวนวันเฉลี่ยต่อขั้นตอน',
  'Bank Transfer': 'โอนธนาคาร',
  Before: 'ก่อนหน้า',
  Billing: 'การเรียกเก็บเงิน',
  'Billing & Plan': 'การเรียกเก็บเงินและแพ็กเกจ',
  'Billing Cycle': 'รอบการเรียกเก็บเงิน',
  'Billing Details': 'รายละเอียดการเรียกเก็บเงิน',
  'CCPA opt-out right for California residents':
    'สิทธิ์ไม่ยินยอม (CCPA) สำหรับผู้อยู่อาศัยในแคลิฟอร์เนีย',
  'Cancel Subscription': 'ยกเลิกการสมัคร',
  Card: 'บัตรเครดิต',
  Cash: 'เงินสด',
  Cheque: 'เช็ค',
  'Clean and bright for well-lit workspaces': 'สว่างและสะอาด เหมาะกับพื้นที่ทำงานที่มีแสงดี',
  'Click the slots above to add diamonds': 'คลิกช่องด้านบนเพื่อเพิ่มเพชร',
  Closed: 'ปิดแล้ว',
  'Comp Conversions': 'อัตราการปิดดีล',
  'Compare up to 4 diamonds side-by-side with spec highlights':
    'เปรียบเทียบเพชรได้สูงสุด 4 เม็ดพร้อมไฮไลต์สเปก',
  'Connected Integrations': 'การเชื่อมต่อที่เชื่อมต่ออยู่',
  'Consent & Preferences': 'ความยินยอมและการตั้งค่า',
  'Consignment Tracking': 'ติดตามการฝากขาย',
  'Consignment Value': 'มูลค่าการฝากขาย',
  'Contact Settings': 'ตั้งค่าการติดต่อ',
  'Contact support to change email': 'ติดต่อฝ่ายสนับสนุนเพื่อเปลี่ยนอีเมล',
  'Control which emails you receive from us': 'เลือกรับอีเมลจากเราได้ตามต้องการ',
  'Conversion Rate per Stage': 'อัตราการเปลี่ยนต่อขั้นตอน',
  'Conversion Summary': 'สรุปการเปลี่ยน',
  'Convert Demo to Active': 'แปลงเดโมเป็นบัญชีใช้งาน',
  'Cookie Policy': 'นโยบายคุกกี้',
  'Cookie Preferences': 'ตั้งค่าคุกกี้',
  'Current plan, usage, and billing overview': 'ภาพรวมแพ็กเกจ การใช้งาน และการเรียกเก็บเงิน',
  'Devices currently signed in to your account': 'อุปกรณ์ที่ล็อกอินบัญชีของคุณอยู่',
  'Diamond Comparison': 'เปรียบเทียบเพชร',
  'Email Us': 'อีเมลถึงเรา',
  'Export Format': 'รูปแบบการส่งออก',
  'Export Notes': 'หมายเหตุการส่งออก',
  Feature: 'ฟีเจอร์',
  'How Tax Rates Are Applied': 'วิธีการคิดอัตราภาษี',
  'How we collect, use, and protect your data': 'วิธีที่เราเก็บ ใช้ และคุ้มครองข้อมูลของคุณ',
  'Include Modules': 'โมดูลที่รวมอยู่',
  'Information about cookies and tracking technologies': 'ข้อมูลเกี่ยวกับคุกกี้และเทคโนโลยีติดตาม',
  'LINE Us': 'LINE หาเรา',
  'Licence Status': 'สถานะไลเซนส์',
  'MRR, ARR, churn, conversion and usage analytics':
    'วิเคราะห์ MRR, ARR, การยกเลิก, การเปลี่ยน และการใช้งาน',
  'Manage analytics, marketing, and preference cookies':
    'จัดการคุกกี้วิเคราะห์ การตลาด และการตั้งค่า',
  'Manage pricing tiers, billing cycles, and feature inclusions':
    'จัดการระดับราคา รอบการเรียกเก็บเงิน และฟีเจอร์ที่รวมอยู่',
  'Manage privacy preferences and view legal policies':
    'จัดการการตั้งค่าความเป็นส่วนตัวและดูนโยบาย',
  'Manage third-party services connected to your account':
    'จัดการบริการของบุคคลที่สามที่เชื่อมต่อกับบัญชี',
  'Manage your plan, billing, and account usage':
    'จัดการแพ็กเกจ การเรียกเก็บเงิน และการใช้งานบัญชี',
  'Memos, holds, and invoices in one view': 'เมโม การจอง และใบแจ้งหนี้ในหน้าเดียว',
  'Messages from the contact form will appear here': 'ข้อความจากฟอร์มติดต่อจะปรากฏที่นี่',
  'Monthly Price': 'ราคารายเดือน',
  'Next Billing Date': 'วันเรียกเก็บเงินถัดไป',
  'No consignment items match the selected filters': 'ไม่มีรายการฝากขายที่ตรงกับตัวกรอง',
  'No email accounts yet': 'ยังไม่มีบัญชีอีเมล',
  'No inactive or churned accounts in the last 30 days.':
    'ไม่มีบัญชีที่ไม่ใช้งานหรือยกเลิกใน 30 วันล่าสุด',
  'Paid customers will appear here after subscription purchase':
    'ลูกค้าที่ชำระเงินจะปรากฏที่นี่หลังซื้อแพ็กเกจ',
  'Payments recorded during demo conversions will appear here':
    'การชำระเงินจากการแปลงเดโมจะปรากฏที่นี่',
  'Recent authentication and account activity': 'การยืนยันตัวตนและกิจกรรมบัญชีล่าสุด',
  'Recommended for secure connections': 'แนะนำสำหรับการเชื่อมต่อที่ปลอดภัย',
  'Reduced eye strain in low-light environments': 'ลดความเมื่อยตาในที่แสงน้อย',
  'Save 20%': 'ประหยัด 20%',
  'Send a test email to verify your SMTP configuration.':
    'ส่งอีเมลทดสอบเพื่อตรวจสอบการตั้งค่า SMTP',
  'Session tracking for other devices is not available yet':
    'การติดตามอุปกรณ์อื่นยังไม่พร้อมใช้งาน',
  'Subscription Plan': 'แพ็กเกจสมาชิก',
  'This panel is not linked from any public page': 'แผงนี้ไม่ได้เชื่อมโยงจากหน้าสาธารณะใด ๆ',
  'Transparent Pricing for Diamond Merchants': 'ราคาโปร่งใสสำหรับผู้ค้าเพชร',
  'Update your personal details and contact information': 'อัปเดตข้อมูลส่วนตัวและช่องทางติดต่อ',
  'Usage refreshes at the start of each billing period.': 'การใช้งานรีเซ็ตต้นรอบการเรียกเก็บเงิน',
  'Usage resets at the start of each billing period.': 'การใช้งานรีเซ็ตต้นรอบการเรียกเก็บเงิน',
  'Usage vs Plan Limits': 'การใช้งานเทียบขีดจำกัดแพ็กเกจ',
  'Use TLS/STARTTLS': 'ใช้ TLS/STARTTLS',
  'WhatsApp Us': 'WhatsApp หาเรา',
  'Will be downloaded as': 'จะดาวน์โหลดเป็น',
  'Your active subscription, usage, and billing details':
    'การสมัคร การใช้งาน และรายละเอียดการเรียกเก็บเงิน',
  'billed monthly': 'เรียกเก็บรายเดือน',
  country: 'ประเทศ',
  'customer tier': 'ระดับลูกค้า',
  'not provisioned': 'ยังไม่จัดสรร',
  'per 1 USD': 'ต่อ 1 USD',
  'vs. Cheapest': 'เทียบราคาถูกสุด',

  // Deal pipeline
  'Add Deal': 'เพิ่มดีล',
  'Edit Deal': 'แก้ไขดีล',
  Prospect: 'ผู้สนใจ',
  Negotiation: 'การเจรจา',
  'Closed Won': 'ปิดการขายสำเร็จ',
  'Closed Lost': 'ปิดการขายไม่สำเร็จ',
  'Weighted pipeline value:': 'มูลค่าไปป์ไลน์ถ่วงน้ำหนัก:',
  weighted: 'ถ่วงน้ำหนัก',
  deal: 'ดีล',
  deals: 'ดีล',

  // Pipeline analytics
  'Total Deals': 'ดีลทั้งหมด',

  // User management
  'Add User': 'เพิ่มผู้ใช้',
  'Create, edit, and manage user accounts and role assignments':
    'สร้าง แก้ไข และจัดการบัญชีผู้ใช้และการมอบหมายบทบาท',
  'Total Users': 'ผู้ใช้ทั้งหมด',
  Owners: 'เจ้าของ',
  Managers: 'ผู้จัดการ',
  Appraisers: 'ผู้ประเมิน',
  'Audit Trail': 'บันทึกการตรวจสอบ',
  'Search users...': 'ค้นหาผู้ใช้...',

  // Billing
  'No Active Plan': 'ไม่มีแพ็กเกจที่ใช้งาน',
  'Period Start': 'วันเริ่มรอบ',
  'Next Payment': 'ชำระครั้งถัดไป',
  'Days Remaining': 'วันที่เหลือ',
  'seats used': 'ที่นั่งที่ใช้',
  'seats available': 'ที่นั่งว่าง',
  'Add Seats': 'เพิ่มที่นั่ง',
  'Upgrade Plan': 'อัปเกรดแพ็กเกจ',
  'Change Plan': 'เปลี่ยนแพ็กเกจ',
  'View Invoices': 'ดูใบแจ้งหนี้',
  'Payments secured via Bank Transfer / PromptPay / Payment Gateways':
    'ชำระเงินปลอดภัยผ่านโอนธนาคาร / พร้อมเพย์ / ผู้ให้บริการชำระเงิน',
  'View all': 'ดูทั้งหมด',
  'Account Owner': 'เจ้าของบัญชี',
  'No Plan': 'ไม่มีแพ็กเกจ',
  used: 'ใช้แล้ว',
  Items: 'รายการ',
  GB: 'GB',
  Storage: 'พื้นที่จัดเก็บ',
  'Team Seats': 'ที่นั่งทีมงาน',
  คำฟีเจอร์: 'ฟีเจอร์',

  // Subscription
  ไม่มีแพ็กเกจที่ใช้งาน: 'ไม่มีแพ็กเกจที่ใช้งาน',
  'No subscription': 'ยังไม่มีแพ็กเกจ',
  'Account ID': 'รหัสบัญชี',
  'Email Verified': 'ยืนยันอีเมลแล้ว',
  รอสร้างบัญชี: 'รอสร้างบัญชี',
  'Member Since': 'เป็นสมาชิกตั้งแต่',
  'Current Period Start': 'วันเริ่มรอบปัจจุบัน',
  'Amount Due': 'ยอดที่ต้องชำระ',
  users: 'ผู้ใช้',
  items: 'รายการ',
  'Contact Support': 'ติดต่อฝ่ายสนับสนุน',
  'Email support included with your plan.': 'ฝ่ายสนับสนุนทางอีเมลรวมอยู่ในแพ็กเกจของคุณ',

  // Customer portal
  'Read-only branded view — active memos, holds, and invoices per customer':
    'มุมมองแบรนด์แบบอ่านอย่างเดียว — เมโม การจอง และใบแจ้งหนี้ของลูกค้าแต่ละราย',
  'Search customers...': 'ค้นหาลูกค้า...',
  memo: 'เมโม',
  hold: 'การจอง',
  overdue: 'เกินกำหนด',
  Slot: 'ช่อง',

  // Super admin login (renders outside the language provider)
  'Restricted access — authorised personnel only':
    'เข้าถึงจำกัด — สำหรับผู้ที่ได้รับอนุญาตเท่านั้น',
  'Enter username': 'กรอกชื่อผู้ใช้',
  'Enter password': 'กรอกรหัสผ่าน',
  'Access Panel': 'เข้าสู่แผงควบคุม',
  'Authenticating...': 'กำลังยืนยันตัวตน...',
  'in Pipeline': 'ในไปป์ไลน์',
  'Across all': 'จากทุก',
  stages: 'ขั้นตอน',
  'Prospect →': 'ผู้สนใจ →',
  'Pipeline Value': 'มูลค่าไปป์ไลน์',
  'Weighted revenue': 'รายได้ถ่วงน้ำหนัก',
  'Overall Conversion': 'อัตราปิดการขายรวม',
  'Prospect→': 'ผู้สนใจ→',
  'Avg Sales Cycle': 'รอบการขายเฉลี่ย',
  'Prospect to close': 'ผู้สนใจถึงปิดการขาย',
  Live: 'สด',
  Qualified: 'ผ่านการคัดกรอง',
  Proposal: 'ข้อเสนอ',
  รายได้: 'รายได้',
  เฉลี่ยวัน: 'เฉลี่ยวัน',
  อัตราปิด: 'อัตราปิด',
  โอกาสสำเร็จ: 'โอกาสสำเร็จ',

  // ── Marketing site (nav, homepage, footer) ─────────────────────────────
  // Nav + CTAs
  Solutions: 'โซลูชัน',
  Pricing: 'ราคา',
  Contact: 'ติดต่อ',
  'Log in': 'เข้าสู่ระบบ',
  'Free Demo': 'ทดลองใช้ฟรี',
  'Book a Demo': 'นัดดูเดโม',
  'Start Free Trial': 'เริ่มทดลองใช้ฟรี',
  'For Retailers': 'สำหรับร้านค้าปลีก',
  'For Wholesalers': 'สำหรับผู้ขายส่ง',
  'For Appraisers': 'สำหรับผู้ประเมินราคา',
  'Multi-Location': 'หลายสาขา',

  // Hero
  'Diamond Merchant CRM — Built for Thailand': 'ซีอาร์เอ็มสำหรับพ่อค้าเพชร — สร้างมาเพื่อไทย',
  'Run Your Diamond Business': 'บริหารธุรกิจเพชรของคุณ',
  'Without the Chaos': 'โดยไม่ต้องวุ่นวาย',
  'Inventory, pricing, CRM, and appraisal — unified in one platform designed for diamond merchants in Bangkok and beyond.':
    'สินค้า ราคา CRM และการประเมินราคา — รวมทุกอย่างไว้ในแพลตฟอร์มเดียวที่ออกแบบสำหรับพ่อค้าเพชรในกรุงเทพฯ และทั่วโลก',
  'No credit card required · 14-day free trial · Cancel anytime':
    'ไม่ต้องใช้บัตรเครดิต · ทดลองใช้ฟรี 14 วัน · ยกเลิกได้ทุกเมื่อ',

  // Trust bar + stats
  'TRUSTED BY MERCHANTS ACROSS THAILAND & SOUTHEAST ASIA':
    'ได้รับความไว้วางใจจากพ่อค้าเพชรทั่วไทยและอาเซียน',
  'DIAMONDS TRACKED': 'เพชรที่ติดตาม',
  'ACTIVE MERCHANTS': 'พ่อค้าที่ใช้งาน',
  'INVENTORY MANAGED': 'มูลค่าสินค้าที่ดูแล',

  // Three pillars
  'Three Pillars of Diamond Business Mastery': 'สามเสาหลักของการบริหารธุรกิจเพชร',
  'Every feature in Glanz CRM maps to a real problem diamond merchants face every day.':
    'ทุกฟีเจอร์ใน Glanz CRM ถูกออกแบบมาแก้ปัญหาจริงที่พ่อค้าเพชรเจอทุกวัน',
  'Inventory Intelligence': 'คลังเพชรอัจฉริยะ',
  'Precision Pricing': 'ตั้งราคาแม่นยำ',
  'Relationship CRM': 'CRM ความสัมพันธ์ลูกค้า',
  'Track every stone by cut, carat, color, clarity, and certificate number. QR scanning, GIA verification, and aging alerts built in.':
    'ติดตามเพชรทุกเม็ดตามรูปทรง กะรัต สี ความสะอาด และเลขใบรับรอง พร้อมสแกน QR ตรวจสอบ GIA และแจ้งเตือนสต็อกเก่าในตัว',
  'Live Rapaport integration with custom markup rules. Set price by shape, quality tier, or client relationship — automatically.':
    'เชื่อมต่อราคา Rapaport สดพร้อมกำหนดกฎเพิ่มราคาของคุณเอง ตั้งราคาตามรูปทรง เกรด หรือความสัมพันธ์ลูกค้า — อัตโนมัติ',
  'Manage wishlists, follow-up tasks, and deal history for every buyer and supplier. Never let a hot lead go cold.':
    'จัดการรายการที่อยากได้ งานติดตามลูกค้า และประวัติดีลของผู้ซื้อและซัพพลายเออร์ทุกราย ไม่มีลูกค้าใหญ่หลุดมืออีก',
  'Unlimited SKUs with 4C attributes': 'สินค้าไม่จำกัดพร้อมสเปก 4C',
  'QR code generation & scanning': 'สร้างและสแกน QR โค้ด',
  'GIA certificate verification': 'ตรวจสอบใบรับรอง GIA',
  'Automatic aging & hold alerts': 'แจ้งเตือนสต็อกเก่าและการจองอัตโนมัติ',
  'Live Rapaport price feed': 'ฟีดราคา Rapaport สด',
  'Custom markup rule engine': 'ระบบกฎเพิ่มราคาแบบกำหนดเอง',
  'Per-client pricing tiers': 'ระดับราคารายลูกค้า',
  'Profit margin calculator': 'เครื่องคำนวณอัตรากำไร',
  'Client & supplier profiles': 'โปรไฟล์ลูกค้าและซัพพลายเออร์',
  'Wishlist & preference tracking': 'ติดตามรายการที่ลูกค้าสนใจ',
  'Automated follow-up reminders': 'แจ้งเตือนติดตามลูกค้าอัตโนมัติ',
  'Deal pipeline & close rates': 'ไปป์ไลน์ดีลและอัตราปิดการขาย',
  'Explore module': 'ดูโมดูล',

  // How it works
  'Up and Running in 3 Steps': 'เริ่มใช้งานได้ใน 3 ขั้นตอน',
  'No IT team required. Most merchants are live within one business day.':
    'ไม่ต้องมีทีมไอที พ่อค้าส่วนใหญ่เริ่มใช้งานจริงได้ภายในหนึ่งวันทำการ',
  'Import Your Inventory': 'นำเข้าสินค้าของคุณ',
  'Set Your Pricing Rules': 'ตั้งกฎราคาของคุณ',
  'Close Deals Faster': 'ปิดดีลได้เร็วขึ้น',
  'Upload a CSV, scan QR codes, or add stones manually. Attach GIA reports and photos in seconds.':
    'อัปโหลด CSV สแกน QR หรือเพิ่มเพชรทีละเม็ด แนบใบรับรอง GIA และรูปภาพได้ในไม่กี่วินาที',
  'Connect to live Rapaport data. Define markup rules by shape, quality, and client tier — prices update automatically.':
    'เชื่อมต่อข้อมูล Rapaport สด กำหนดกฎเพิ่มราคาตามรูปทรง เกรด และระดับลูกค้า — ราคาอัปเดตอัตโนมัติ',
  'Share live inventory links with buyers, track memos and holds, and convert pipeline deals with one click.':
    'ส่งลิงก์สินค้าสดให้ผู้ซื้อ ติดตามเมโมและการจอง และเปลี่ยนดีลในไปป์ไลน์เป็นการขายในคลิกเดียว',

  // Module integration section
  'Every Module, Deeply Integrated': 'ทุกโมดูลเชื่อมต่อกันอย่างลึกซึ้ง',
  'Switch between modules — data flows seamlessly between them all.':
    'สลับระหว่างโมดูล — ข้อมูลไหลถึงกันหมดอย่างราบรื่น',
  'Screenshot coming soon': 'ภาพหน้าจอเร็ว ๆ นี้',
  Module: 'โมดูล',
  'See full': 'ดูทั้งหมด',
  docs: 'เอกสาร',

  // Feature tab checks
  '4C attribute tracking': 'ติดตามสเปก 4C',
  'QR code scanning': 'สแกน QR โค้ด',
  'GIA certificate link': 'เชื่อมโยงใบรับรอง GIA',
  'Aging & hold alerts': 'แจ้งเตือนสต็อกเก่าและการจอง',
  'Bulk CSV import': 'นำเข้า CSV เป็นชุด',
  'Photo gallery per stone': 'แกลเลอรีรูปต่อเม็ด',
  'Live Rapaport feed': 'ฟีด Rapaport สด',
  'Custom markup rules': 'กฎเพิ่มราคาแบบกำหนดเอง',
  'Per-client tiers': 'ระดับราคารายลูกค้า',
  'Profit margin view': 'มุมมองอัตรากำไร',
  'Currency toggle (THB/USD)': 'สลับสกุลเงิน (THB/USD)',
  'Discount approval flow': 'ขั้นตอนอนุมัติส่วนลด',
  'Wishlist management': 'จัดการรายการที่ลูกค้าสนใจ',
  'Follow-up task queue': 'คิวงานติดตามลูกค้า',
  'Deal pipeline stages': 'ขั้นตอนไปป์ไลน์ดีล',
  'Email automation': 'อีเมลอัตโนมัติ',
  'Activity timeline': 'ไทม์ไลน์กิจกรรม',
  'Appraisal report builder': 'สร้างรายงานประเมินราคา',
  'GIA verification API': 'API ตรวจสอบ GIA',
  'Certificate archive': 'คลังใบรับรอง',
  'Compliance audit log': 'บันทึกตรวจสอบการปฏิบัติตาม',
  'Digital signature': 'ลายเซ็นดิจิทัล',
  'PDF export': 'ส่งออก PDF',

  // Product tour
  'Interactive Product Tour': 'ทัวร์ผลิตภัณฑ์แบบโต้ตอบ',
  'See Glanz CRM in Action': 'ดู Glanz CRM ในการใช้งานจริง',
  'Walk through a real diamond deal — from adding a stone to closing the sale and reading your P&L.':
    'เดินตามดีลเพชรจริง — ตั้งแต่เพิ่มเพชรจนถึงปิดการขายและอ่านกำไรขาดทุน',
  'Add a Stone': 'เพิ่มเพชร',
  'Set Pricing': 'ตั้งราคา',
  'Issue a Memo': 'ออกเมโม',
  'Track Clients': 'ติดตามลูกค้า',
  'Close & Report': 'ปิดดีลและรายงาน',
  'Add any diamond in under 30 seconds': 'เพิ่มเพชรได้ในไม่ถึง 30 วินาที',
  'Scan a QR code or type the certificate number. Glanz auto-fills cut, carat, color, and clarity from the GIA database — no manual entry.':
    'สแกน QR หรือพิมพ์เลขใบรับรอง Glanz เติมรูปทรง กะรัต สี และความสะอาดจากฐานข้อมูล GIA ให้อัตโนมัติ — ไม่ต้องกรอกเอง',
  'Live Rapaport pricing, your markup rules': 'ราคา Rapaport สด พร้อมกฎเพิ่มราคาของคุณ',
  'Connect once to Rapaport. Define markup rules by shape, quality tier, or client. Prices recalculate automatically every time the feed updates.':
    'เชื่อมต่อ Rapaport ครั้งเดียว กำหนดกฎเพิ่มราคาตามรูปทรง เกรด หรือลูกค้า ราคาคำนวณใหม่อัตโนมัติทุกครั้งที่ฟีดอัปเดต',
  'Memo out stones with one click': 'ออกเมโมเพชรในคลิกเดียว',
  'Select stones, pick a client, set a return date. Glanz tracks every memo in real time — and alerts you 48 hours before anything is due back.':
    'เลือกเพชร เลือกลูกค้า กำหนดวันคืนสินค้า Glanz ติดตามเมโมทุกใบแบบเรียลไทม์ — และแจ้งเตือนล่วงหน้า 48 ชั่วโมงก่อนครบกำหนดคืน',
  'Every client relationship, in one place': 'ความสัมพันธ์ลูกค้าทุกราย ในที่เดียว',
  'Wishlists, purchase history, follow-up tasks, and deal stage — all linked to each contact. Never forget a hot lead or a promised callback.':
    'รายการที่สนใจ ประวัติซื้อ งานติดตาม และขั้นตอนดีล — เชื่อมกับผู้ติดต่อทุกราย ไม่มีลืมลูกค้าใหญ่หรือคำสัญญาโทรกลับ',
  'Close deals and see your numbers instantly': 'ปิดดีลและเห็นตัวเลขทันที',
  'Convert a pipeline deal to a sale in one click. Profit & loss, inventory aging, and revenue charts update in real time — no spreadsheets needed.':
    'เปลี่ยนดีลในไปป์ไลน์เป็นการขายในคลิกเดียว กำไรขาดทุน อายุสินค้า และกราฟรายได้อัปเดตเรียลไทม์ — ไม่ต้องใช้สเปรดชีต',
  'Scan QR or enter cert number': 'สแกน QR หรือกรอกเลขใบรับรอง',
  'Auto-fill from GIA database': 'เติมอัตโนมัติจากฐานข้อมูล GIA',
  'Attach photos in one tap': 'แนบรูปในแตะเดียว',
  'Live Rapaport feed integration': 'เชื่อมต่อฟีด Rapaport สด',
  'Per-shape & per-tier markup rules': 'กฎเพิ่มราคาตามรูปทรงและเกรด',
  'Instant margin calculator': 'คำนวณอัตรากำไรทันที',
  'One-click memo issuance': 'ออกเมโมคลิกเดียว',
  '48-hour return alerts': 'แจ้งเตือนคืนสินค้าล่วงหน้า 48 ชม.',
  'Full memo history per client': 'ประวัติเมโมครบทุกลูกค้า',
  'Deal pipeline with stage tracking': 'ไปป์ไลน์ดีลพร้อมติดตามขั้นตอน',
  'One-click deal conversion': 'เปลี่ยนดีลเป็นขายคลิกเดียว',
  'Real-time P&L dashboard': 'แดชบอร์ดกำไรขาดทุนเรียลไทม์',
  'PDF invoice generation': 'สร้างใบแจ้งหนี้ PDF',
  STEP: 'ขั้นตอน',
  OF: 'จาก',
  'Try it live — Book a Demo': 'ลองใช้จริง — นัดดูเดโม',

  // Tour mock-UI labels
  'Stone ID': 'รหัสเพชร',
  'Color / Clarity': 'สี / ความสะอาด',
  'GIA Certificate': 'ใบรับรอง GIA',
  'Rapaport Base': 'ราคาฐาน Rapaport',
  'Markup Rule': 'กฎเพิ่มราคา',
  'Calculated Price': 'ราคาที่คำนวณ',
  'Client Tier': 'ระดับลูกค้า',
  'Final Quote': 'ราคาเสนอสุดท้าย',
  Client: 'ลูกค้า',
  Stones: 'จำนวนเม็ด',
  Issued: 'ออกเมื่อ',
  Wishlist: 'รายการที่สนใจ',
  'Last Purchase': 'ซื้อล่าสุด',
  'Pipeline Stage': 'ขั้นตอนไปป์ไลน์',
  'Follow-up': 'ติดตาม',
  'Lifetime Value': 'มูลค่าลูกค้ารวม',
  'Sale Price': 'ราคาขาย',
  'Cost Basis': 'ต้นทุน',
  'Gross Profit': 'กำไรขั้นต้น',
  'GIA Verified': 'รับรอง GIA แล้ว',
  'Live Feed': 'ฟีดสด',
  'Auto-Reminder': 'แจ้งเตือนอัตโนมัติ',
  'Smart Follow-up': 'ติดตามอัจฉริยะ',
  'Instant P&L': 'กำไรขาดทุนทันที',

  // Testimonials
  'Merchants Who Made the Switch': 'พ่อค้าที่เปลี่ยนมาใช้ Glanz',
  'Glanz CRM cut our memo tracking time by 80%. We used to lose stones in spreadsheets — now every diamond has a real-time status.':
    'Glanz CRM ลดเวลาติดตามเมโมของเราลง 80% ก่อนหน้านี้เพชรหายในสเปรดชีตเป็นประจำ — ตอนนี้ทุกเม็ดมีสถานะแบบเรียลไทม์',
  "The Rapaport integration alone is worth the subscription. Pricing 200+ stones used to take half a day. Now it's 10 minutes.":
    'การเชื่อมต่อ Rapaport อย่างเดียวก็คุ้มค่าสมาชิกแล้ว ก่อนหน้านี้ตั้งราคาเพชร 200+ เม็ดใช้เวลาครึ่งวัน ตอนนี้เหลือ 10 นาที',
  'As an appraiser, the GIA verification and certificate management features are exactly what I needed. Professional and fast.':
    'ในฐานะผู้ประเมินราคา ฟีเจอร์ตรวจสอบ GIA และจัดการใบรับรองคือสิ่งที่ฉันต้องการพอดี มืออาชีพและรวดเร็ว',
  'Owner, Silom Diamond Co.': 'เจ้าของ สีลม ไดมอนด์ โค.',
  'Director, Bangkok Gem Wholesale': 'กรรมการผู้จัดการ บางกอก เจม โฮลเซล',
  'Principal Appraiser, GIA Alumni': 'หัวหน้าผู้ประเมิน ศิษย์เก่า GIA',
  Retailer: 'ร้านค้าปลีก',
  Wholesaler: 'ผู้ขายส่ง',
  Appraiser: 'ผู้ประเมินราคา',

  // Solutions grid
  'Built for Every Type of Diamond Business': 'สร้างมาเพื่อธุรกิจเพชรทุกรูปแบบ',
  "Whether you're a solo appraiser or a multi-branch wholesaler, Glanz CRM adapts to you.":
    'ไม่ว่าคุณจะเป็นผู้ประเมินอิสระหรือผู้ขายส่งหลายสาขา Glanz CRM ปรับตามคุณ',
  Retailers: 'ร้านค้าปลีก',
  Wholesalers: 'ผู้ขายส่ง',
  'Showroom inventory, client wishlists, and POS-ready sales flow.':
    'สินค้าในโชว์รูม รายการที่ลูกค้าสนใจ และกระบวนการขายพร้อมใช้',
  'Bulk memo management, supplier payables, and volume pricing.':
    'จัดการเมโมเป็นชุด บัญชีเจ้าหนี้ซัพพลายเออร์ และราคาขายส่ง',
  'GIA-linked appraisal reports, certificate archive, and compliance logs.':
    'รายงานประเมินเชื่อม GIA คลังใบรับรอง และบันทึกการปฏิบัติตาม',
  'Unified inventory across branches with role-based access per location.':
    'สินค้ารวมทุกสาขาพร้อมสิทธิ์การเข้าถึงแยกตามสาขา',
  'Learn more': 'ดูเพิ่มเติม',

  // Pricing teaser + CTA + form
  'Simple, Transparent Pricing': 'ราคาโปร่งใส เข้าใจง่าย',
  'No hidden fees. 14-day free trial on all plans. Cancel anytime.':
    'ไม่มีค่าธรรมเนียมแอบแฝง ทดลองใช้ฟรี 14 วันทุกแพ็กเกจ ยกเลิกได้ทุกเมื่อ',
  'Solo traders': 'นักค้าอิสระ',
  'Growing teams': 'ทีมที่กำลังเติบโต',
  'Large operations': 'ธุรกิจขนาดใหญ่',
  '/month': '/ เดือน',
  'Compare all features': 'เปรียบเทียบทุกฟีเจอร์',
  'Compare All Features': 'เปรียบเทียบทุกฟีเจอร์',
  'Ready to Modernize Your Diamond Business?': 'พร้อมยกระดับธุรกิจเพชรของคุณหรือยัง?',
  'Join 500+ merchants in Bangkok and across Thailand already using Glanz CRM to track inventory, close deals, and grow revenue.':
    'เข้าร่วมกับพ่อค้าเพชรกว่า 500 รายในกรุงเทพฯ และทั่วไทยที่ใช้ Glanz CRM ติดตามสินค้า ปิดดีล และเติบโตด้านรายได้',
  'Start Your Free Trial': 'เริ่มทดลองใช้ฟรี',
  '14 days free. No credit card required.': 'ฟรี 14 วัน ไม่ต้องใช้บัตรเครดิต',
  'Full Name *': 'ชื่อ-นามสกุล *',
  'Email *': 'อีเมล *',
  'Phone / LINE ID': 'โทรศัพท์ / LINE ID',
  'Business Name': 'ชื่อธุรกิจ',
  City: 'จังหวัด / เมือง',
  'John Smith': 'สมชาย ใจดี',
  'Siam Diamond Co.': 'สยาม ไดมอนด์ โค.',
  Bangkok: 'กรุงเทพมหานคร',
  'Sending...': 'กำลังส่ง...',
  'No credit card required · Cancel anytime': 'ไม่ต้องใช้บัตรเครดิต · ยกเลิกได้ทุกเมื่อ',
  "You're on the list!": 'คุณอยู่ในลิสต์ของเราแล้ว!',
  "We'll be in touch within 24 hours.": 'เราจะติดต่อกลับภายใน 24 ชั่วโมง',
  'Something went wrong. Please try again.': 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง',

  // Footer
  'The diamond merchant management platform built for Thailand and the global gem trade.':
    'แพลตฟอร์มบริหารธุรกิจเพชรที่สร้างมาเพื่อไทยและตลาดอัญมณีโลก',
  Subscribe: 'สมัครรับข่าวสาร',

  // ── Homepage mock-UI labels & statuses (v10 coverage) ─────────────────
  'Total Inventory': 'สินค้าทั้งหมด',
  'Active Memos': 'เมโมที่ใช้งาน',
  'Diamonds Tracked': 'เพชรที่ติดตาม',
  'Active Merchants': 'พ่อค้าที่ใช้งาน',
  'Inventory Managed': 'สินค้าที่บริหาร',
  'Shape / Carat': 'รูปทรง / กะรัต',
  '+12 this week': '+12 สัปดาห์นี้',
  '↑ 18% vs last month': '↑ 18% จากเดือนที่แล้ว',
  '2M+ Diamonds Tracked': 'ติดตามเพชรกว่า 2 ล้านเม็ด',
  'I agree to the': 'ข้าพเจ้ายอมรับ',
  and: 'และ',
  'On Memo': 'ออกเมโม',
  'On Hold': 'จองไว้',
  Available: 'พร้อมขาย',
  'Sale #': 'เลขที่ขาย',
  '3 expiring soon': '3 รายการใกล้หมดอายุ',

  // ── Contact page ──────────────────────────────────────────────────────
  'Get in Touch': 'ติดต่อเรา',
  "We'd Love to Hear From You": 'เรายินดีรับฟังจากคุณ',
  'Send Us a Message': 'ส่งข้อความถึงเรา',
  'Contact Information': 'ข้อมูลการติดต่อ',
  'Office Hours': 'เวลาทำการ',
  'Book a product demo': 'นัดดูเดโมสินค้า',
  'Pricing question': 'สอบถามราคา',
  'Technical support': 'ซัพพอร์ทเทคนิค',
  'Partnership inquiry': 'ข้อเสนอร่วมมือ',
  'Billing & subscription': 'การชำระเงินและสมาชิก',
  'Select a topic...': 'เลือกหัวข้อ...',
  'Send Message': 'ส่งข้อความ',
  'Please fill in all required fields.': 'กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน',
  'Please agree to the Privacy Policy so we can process your message.':
    'กรุณายอมรับนโยบายความเป็นส่วนตัวเพื่อให้เราดำเนินการข้อความของคุณได้',
  'Failed to send message.': 'ส่งข้อความไม่สำเร็จ',
  'Message Sent!': 'ส่งข้อความสำเร็จ!',
  'Looking for something specific?': 'กำลังมองหาอะไรเป็นพิเศษ?',
  'View pricing plans': 'ดูแพ็กเกจราคา',
  'Sign in to your account': 'เข้าสู่ระบบบัญชีของคุณ',

  // ── Demo page ─────────────────────────────────────────────────────────
  '14-day full access — all features unlocked': 'เข้าใช้ได้เต็ม 14 วัน — ปลดล็อกทุกฟีเจอร์',
  'Live inventory walkthrough': 'เดินดูสินค้าแบบสด',
  'Rapaport pricing demo': 'เดโมราคา Rapaport',
  'CRM & pipeline tour': 'ทัวร์ CRM และไปป์ไลน์',
  'Role & access setup': 'ตั้งค่าบทบาทและสิทธิ์',
  'Configure your team structure in real time': 'ตั้งค่าโครงสร้างทีมแบบสด ๆ',
  'Confirmation email sent immediately': 'ส่งอีเมลยืนยันทันที',
  'Create Demo Account →': 'สร้างบัญชีทดลอง →',
  'Dedicated onboarding support': 'ซัพพอร์ทเริ่มต้นแบบเฉพาะ',
  'No credit card required': 'ไม่ต้องใช้บัตรเครดิต',
  'See how your stones would look in the system': 'ดูว่าเพชรของคุณจะเป็นอย่างไรในระบบ',
  'Understand how deals flow from lead to close': 'เข้าใจการไหลของดีลจากลีดถึงปิดการขาย',
  'Watch live price calculations with your markup rules': 'ดูการคำนวณราคาสดด้วยกฎมาร์จินของคุณ',
  'Very Strong': 'แข็งแกร่งมาก',

  // ── Security page ─────────────────────────────────────────────────────
  'Glanz CRM security': 'ความปลอดภัย Glanz CRM',
  'Encryption, access controls, and infrastructure practices behind Glanz CRM.':
    'การเข้ารหัส การควบคุมการเข้าถึง และแนวปฏิบัติโครงสร้างพื้นฐานเบื้องหลัง Glanz CRM',
  'Encryption in Transit': 'การเข้ารหัสขณะส่งข้อมูล',
  'Encryption at Rest': 'การเข้ารหัสขณะจัดเก็บ',
  'Access Controls': 'การควบคุมการเข้าถึง',
  'Role-based access: Owner, Manager, and Appraiser roles see only what they need.':
    'สิทธิ์ตามบทบาท: เจ้าของ ผู้จัดการ และผู้ประเมิน เห็นเฉพาะสิ่งที่จำเป็น',
  'Session tokens and secrets are server-side only and never shipped to the browser.':
    'โทเคนเซสชันและความลับอยู่ฝั่งเซิร์ฟเวอร์เท่านั้น ไม่ถูกส่งไปยังเบราว์เซอร์',
  'Data Center & Backups': 'ศูนย์ข้อมูลและการสำรองข้อมูล',
  'Automated daily database backups with point-in-time recovery.':
    'สำรองฐานข้อมูลทุกวันอัตโนมัติ พร้อมกู้คืนตามจุดเวลา',
  'Database backups and storage are encrypted at rest by our infrastructure provider.':
    'การสำรองและจัดเก็บฐานข้อมูลถูกเข้ารหัสขณะพักโดยผู้ให้บริการโครงสร้างพื้นฐาน',
  'Incident Response': 'การรับมือเหตุการณ์',

  // ── Signup page ───────────────────────────────────────────────────────
  'Basic P&L': 'กำไรขาดทุนพื้นฐาน',
  'Full CRM + pipeline': 'CRM เต็มรูปแบบ + ไปป์ไลน์',
  'Up to 3 users': 'ผู้ใช้ได้ถึง 3 คน',
  'Billing cycle and next payment date': 'รอบบิลและวันชำระถัดไป',
  'Support contact and onboarding guide': 'ช่องทางซัพพอร์ทและคู่มือเริ่มต้น',
  'Your login URL and credentials': 'URL เข้าสู่ระบบและข้อมูลรับรองของคุณ',
  'Welcome Aboard! Your Glanz CRM Account is Active — Login Details Inside':
    'ยินดีต้อนรับ! บัญชี Glanz CRM ของคุณเปิดใช้งานแล้ว — รายละเอียดเข้าสู่ระบบอยู่ด้านใน',

  // ── Nav / footer stragglers ──────────────────────────────────────
  'Appraisal Tools': 'เครื่องมือประเมินราคา',
  'Terms of Service': 'เงื่อนไขการใช้บริการ',
  'User profile and password management': 'บริหารโปรไฟล์ผู้ใช้และรหัสผ่าน',
  'Toggle menu': 'เปิด/ปิดเมนู',
  // Solutions page remaining (headings, titles, challenges, descs)
  'Solutions by Business Type': 'โซลูชันตามประเภทธุรกิจ',
  // Homepage remaining
  'See Interactive Demo': 'ดูเดโมแบบโต้ตอบ',
  'Tour progress': 'ความคืบหน้าทัวร์',
  'Trusted by merchants across Thailand & Southeast Asia':
    'เชื่อมั่นโดยพ่อค้าเพชรทั่วไทยและเอเชียตะวันออกเฉียงใต้',
  'Round Brilliant': 'เจียระไนกลม',
  // Tour mock-UI split nodes + note
  'Call tomorrow 2 PM': 'โทรนัดพรุ่งนี้ บ่าย 2 โมง',

  // About page remaining (hero, buttons, module names, descs)
  'About & Features': 'เกี่ยวกับและฟีเจอร์',
  'Complete overview of the Glanz CRM Platform': 'ภาพรวมทั้งหมดของแพลตฟอร์ม Glanz CRM',
  'Download PDF': 'ดาวน์โหลด PDF',
  'Generated on': 'สร้างเมื่อ',
  'About the Platform': 'เกี่ยวกับแพลตฟอร์ม',
  Database: 'ฐานข้อมูล',
  Dashboard: 'แดชบอร์ด',
  'Real-time business overview with live KPIs, revenue charts, and diamond inventory analytics.':
    'ภาพรวมธุรกิจแบบเรียลไทม์ พร้อม KPI กราฟรายได้ และการวิเคราะห์สินค้าเพชร',
  'Full lifecycle management of diamond stock with rich media, 3D viewing, and QR code support.':
    'บริหารสต็อกเพชรครบวงจร พร้อมมีเดีย 3D และ QR โค้ด',
  Compliance: 'การกำกับดูแล',
  Consignment: 'การฝากขาย',

  // Contact page remaining (LINE card, days, location)
  'Chat with us on LINE': 'แชทกับเราทาง LINE',
  Monday: 'จันทร์',
  Tuesday: 'อังคาร',
  Wednesday: 'พุธ',
  Thursday: 'พฤหัสบดี',
  Friday: 'ศุกร์',
  Saturday: 'เสาร์',
  Sunday: 'อาทิตย์',
  Thailand: 'ประเทศไทย',

  // Signup page remaining
  'Get Started Today': 'เริ่มใช้งานวันนี้',
  'Choose Your Plan': 'เลือกแพ็กเกจของคุณ',
  'Full access immediately after signup. Cancel anytime.':
    'เข้าใช้งานเต็มรูปแบบทันทีหลังสมัคร ยกเลิกได้ทุกเมื่อ',

  // Security page remaining
  'Security at Glanz CRM': 'ความปลอดภัยที่ Glanz CRM',
  'Your diamond inventory, client records, and pricing data are the core of your business. Here is how we protect them.':
    'สินค้าเพชร ข้อมูลลูกค้า และข้อมูลราคา คือหัวใจของธุรกิจคุณ นี่คือวิธีที่เราปกป้องมัน',
  Authentication: 'การยืนยันตัวตน',
  'Password authentication handled by Supabase Auth with secure, httpOnly session cookies.':
    'การยืนยันรหัสผ่านโดย Supabase Auth พร้อมคุกกีเซสชัน httpOnly ที่ปลอดภัย',
  'Affected customers are notified of any data incident without undue delay, per Thai PDPA requirements.':
    'ลูกค้าที่ได้รับผลกระทบจะได้รับแจ้งทันทีเมื่อเกิดเหตุข้อมูลรั่วไหล ตามข้อกำหนด PDPA ของไทย',

  // Demo page remaining
  'Live Product Demo': 'เดโมสดสินค้า',
  'See Glanz CRM': 'ชม Glanz CRM',
  'in Action': 'ทำงานจริง',
  'Sign up for a 14-day free demo. No credit card required. Full access to all features.':
    'สมัครทดลองใช้ฟรี 14 วัน ไม่ต้องใช้บัตรเครดิต เข้าถึงทุกฟีเจอร์',
  'Start Your Free Demo': 'เริ่มทดลองใช้ฟรี',
  'WHAT MERCHANTS SAY': 'เสียงจากพ่อค้าเพชร',
  'What merchants say': 'เสียงจากพ่อค้าเพชร',
  'Passwords match': 'รหัสผ่านตรงกัน',
  optional: '(ไม่บังคับ)',
  '(optional)': '(ไม่บังคับ)',
  'Find the Right Plan for Your Business': 'เลือกแพ็กเกจที่เหมาะกับธุรกิจของคุณ',
  "Book a demo and we'll recommend the right setup for your specific operation.":
    'นัดดูเดโมแล้วเราจะแนะนำการตั้งค่าที่เหมาะกับการทำงานของคุณ',
  'Built for Your Type of Diamond Business': 'สร้างมาเพื่อธุรกิจเพชรแบบของคุณ',
  'Common challenges': 'ปัญหาที่พบบ่อย',
  'How Glanz CRM solves it': 'วิธีที่ Glanz CRM แก้ปัญหา',
  'Clients ask "do you have a 1.5ct round E VS1?" — and you can\'t answer instantly':
    'ลูกค้าถาม "มีเพชรกลม 1.5 กะรัต E VS1 ไหม" — แล้วคุณตอบทันทีไม่ได้',
  "Clients ask for historical valuations and you can't find them":
    'ลูกค้าขอดูมูลค่าย้อนหลัง แต่คุณหาไม่เจอ',
  "Manage what you owe, when it's due, and payment history.":
    'จัดการยอดที่ต้องชำระ กำหนดชำระ และประวัติการชำระ',
  'Bulk pricing engine': 'เครื่องคำนวณราคาเป็นชุด',
  'Price 500 stones in minutes with Rapaport + custom markup rules.':
    'ตั้งราคาเพชร 500 เม็ดในไม่กี่นาทีด้วย Rapaport + กฎมาร์จินของคุณ',
  'Instant GIA verification': 'ตรวจ GIA ทันที',
  'Verify certificate authenticity with one click via GIA API.':
    'ตรวจสอบใบรับรองด้วยคลิกเดียวผ่าน GIA API',
  "Staff at one location can't see what's available at another":
    'พนักงานที่สาขาหนึ่งมองไม่เห็นสินค้าของอีกสาขา',
  'Staff see only their branch; managers see everything.':
    'พนักงานเห็นเฉพาะสาขาตัวเอง ผู้จัดการเห็นทุกอย่าง',
  // Features page hero + headings (incl. JSX-split halves)
  'Platform Features': 'ฟีเจอร์ของแพลตฟอร์ม',
  'Every Tool a Diamond Merchant Needs': 'ทุกเครื่องมือที่พ่อค้าเพชรต้องการ',
  'Four deeply integrated modules — Inventory, Pricing, CRM, and Appraisal — designed to work together seamlessly.':
    'สี่โมดูลที่เชื่อมต่อกันลึกซึ้ง — คลังสินค้า ราคา CRM และการประเมิน — ออกแบบมาให้ทำงานร่วมกันอย่างราบรื่น',
  'All Four Modules at a Glance': 'ทั้งสี่โมดูลในภาพเดียว',
  "Book a personalized demo and we'll walk you through every module with your use case.":
    'นัดดูเดโมแบบเฉพาะของคุณ เราจะพาชมทุกโมดูลพร้อมกรณีใช้งานของคุณ',
  Precision: 'ความแม่นยำ',
  Relationship: 'ความสัมพันธ์',
  Intelligence: 'อัจฉริยะ',
  Appraisal: 'ประเมินราคา',
  "Tell us about your business and what you're looking for...":
    'บอกเราเกี่ยวกับธุรกิจของคุณและสิ่งที่คุณต้องการ...',
  "We'll get back to you within 24 hours.": 'เราจะติดต่อกลับภายใน 24 ชั่วโมง',
  'Repeat your password': 'ยืนยันรหัสผ่านอีกครั้ง',
  'Min 8 chars, 1 uppercase, 1 number, 1 special':
    'อย่างน้อย 8 ตัว มีตัวพิมพ์ใหญ่ ตัวเลข และสัญลักษณ์',
  'Uppercase letter': 'ตัวพิมพ์ใหญ่',
  'Special character': 'สัญลักษณ์พิเศษ',
  'Diamond CRM Platform': 'แพลตฟอร์มบริหารธุรกิจเพชร',
  'Glanz CRM Platform': 'แพลตฟอร์ม Glanz CRM',

  // ── Features page ────────────────────────────────────────────────
  'Attach multiple images and 360° videos per stone.': 'แนบรูปหลายรูปและวิดีโอ 360° ต่อเม็ด',
  'Automatic notifications when stones have been idle too long.':
    'แจ้งเตือนอัตโนมัติเมื่อสินค้านิ่งนานเกินไป',
  'Complete contact records with purchase history, preferences, and notes.':
    'ข้อมูลติดต่อครบถ้วน พร้อมประวัติซื้อ ความชอบ และโน้ต',
  'Cut, carat, color, clarity, fluorescence, and more — per stone.':
    'ตัด กะรัต สี ความสะอาด ฟลูออเรสเซนส์ และอื่น ๆ — ต่อเม็ด',
  'Different prices for retail, wholesale, and VIP clients — automatically.':
    'ราคาต่างกันสำหรับลูกค้าปลีก ส่ง และ VIP — อัตโนมัติ',
  'Discount approval workflow': 'กระบวนการอนุมัติส่วนลด',
  'Every client relationship, every deal, every follow-up — in one place.':
    'ความสัมพันธ์ลูกค้า ทุกดีล ทุกการติดตาม — ในที่เดียว',
  'Export any appraisal as a branded, professional PDF.':
    'ส่งออกรายงานประเมินเป็น PDF ติดแบรนด์อย่างมืออาชีพ',
  'Full 4C attribute tracking': 'ติดตามคุณสมบัติ 4C ครบถ้วน',
  'Full history of every interaction with each contact.':
    'ประวัติเต็มของทุกการติดต่อกับผู้ติดต่อแต่ละราย',
  'Generate insurance replacement value reports.': 'สร้างรายงานมูลค่าทดแทนสำหรับประกันภัย',
  'Generate professional PDF quotes with your branding in seconds.':
    'สร้างใบเสนอราคา PDF ติดแบรนด์ของคุณในไม่กี่วินาที',
  'Historical value tracking': 'ติดตามมูลค่าย้อนหลัง',
  'Immutable log of all appraisal actions for regulatory compliance.':
    'บันทึกที่แก้ไขไม่ได้ของทุกการประเมิน เพื่อการกำกับดูแล',
  'Import your existing spreadsheet inventory in minutes.':
    'นำเข้าสินค้าจากสเปรดชีตเดิมของคุณในไม่กี่นาที',
  'Insurance valuation': 'การประเมินมูลค่าประกันภัย',
  'Inventory aging reports': 'รายงานอายุสินค้า',
  'Know if a stone is in the safe, on display, on memo, or with a client.':
    'รู้ว่าเพชรอยู่ในตู้เซฟ บนโชว์ ออกเมโม หรืออยู่กับลูกค้า',
  'Link and verify GIA reports directly from the inventory record.':
    'เชื่อมและตรวจสอบรายงาน GIA จากบันทึกสินค้าโดยตรง',
  'Location tracking': 'ติดตามสถานที่เก็บ',
  'Manage what you owe suppliers and when payments are due.':
    'จัดการยอดค้างชำระซัพพลายเออร์และกำหนดชำระ',
  'Memo & consignment tracking': 'ติดตามเมโมและการฝากขาย',
  'Never miss a follow-up with automated reminders and task assignments.':
    'ไม่พลาดการติดตามด้วยการแจ้งเตือนอัตโนมัติและมอบหมายงาน',
  'Photo & video gallery': 'คลังรูปและวิดีโอ',
  'Price history log': 'บันทึกประวัติราคา',
  'Print QR labels, scan with any phone, instantly pull up stone details.':
    'พิมพ์สติกเกอร์ QR สแกนด้วยมือถือทุกเครื่อง เปิดดูรายละเอียดได้ทันที',
  'Professional appraisal reports, GIA-linked and compliance-ready.':
    'รายงานประเมินมืออาชีพ เชื่อม GIA พร้อมใช้ตามกฎระเบียบ',
  'Quotation builder': 'ตัวสร้างใบเสนอราคา',
  'Real-time Rap prices integrated directly into your inventory.':
    'ราคา Rap แบบเรียลไทม์เชื่อมเข้าสินค้าของคุณโดยตรง',
  'Require manager approval for discounts above a threshold.':
    'บังคับอนุมัติจากผู้จัดการสำหรับส่วนลดที่เกินเกณฑ์',
  'See which stones are tying up capital and need attention.':
    'เห็นว่าเพชรเม็ดไหนรั้งเงินทุนและต้องเฝ้าระวัง',
  'See your margin on every stone before you quote.': 'เห็นมาร์จินทุกเม็ดก่อนเสนอราคา',
  'Send personalized emails when new matching stones arrive.':
    'ส่งอีเมลส่วนตัวเมื่อมีเพชรที่ตรงใจใหม่เข้ามา',
  'Set markup by shape, quality tier, carat range, or client type.':
    'ตั้งมาร์จินตามรูปทรง เกรดคุณภาพ ช่วงกะรัต หรือประเภทลูกค้า',
  'Sign reports digitally and send to clients securely.':
    'เซ็นรายงานแบบดิจิทัลและส่งให้ลูกค้าอย่างปลอดภัย',
  'Stop guessing. Price every stone correctly, every time.':
    'หยุดเดา ตั้งราคาทุกเม็ดถูกต้อง ทุกครั้ง',
  'Store and search all certificates with full-text search.':
    'จัดเก็บและค้นหาใบรับรองทั้งหมดแบบค้นข้อความเต็ม',
  'Supplier payables': 'บัญชีเจ้าหนี้ซัพพลายเออร์',
  'Switch between Thai Baht and US Dollar with live exchange rates.':
    'สลับบาท-ดอลลาร์ด้วยอัตราแลกเปลี่ยนสด',
  'Track exactly what each client is looking for and match stones automatically.':
    'รู้ว่าลูกค้าแต่ละคนมองหาอะไร และจับคู่เพชรให้อัตโนมัติ',
  'Track how pricing has changed over time for any stone.':
    'ติดตามการเปลี่ยนแปลงราคาย้อนหลังของทุกเม็ด',
  'Track stones out on memo with automatic aging and return alerts.':
    'ติดตามเพชรที่ออกเมโม พร้อมแจ้งเตือนอายุและวันคืนอัตโนมัติ',
  'Verify certificate authenticity directly from the appraisal form.':
    'ตรวจสอบความถูกต้องของใบรับรองจากฟอร์มประเมินโดยตรง',
  'Visual Kanban pipeline from first contact to closed deal.':
    'ไปป์ไลน์ Kanban แบบภาพ จากติดต่อครั้งแรกถึงปิดดีล',

  // ── Solutions page ───────────────────────────────────────────────
  'Answer any client query in seconds with full 4C filtering.':
    'ตอบลูกค้าได้ในไม่กี่วินาทีด้วยตัวกรอง 4C เต็มรูปแบบ',
  'Appraisal reports are created in Word and hard to archive':
    'รายงานประเมินสร้างใน Word เก็บถาวรยาก',
  'Bulk memo management': 'จัดการเมโมเป็นชุด',
  'Bulk pricing calculations take hours in spreadsheets':
    'คำนวณราคาเป็นชุดในสเปรดชีตใช้เวลาหลายชั่วโมง',
  'Client buy rate analytics': 'วิเคราะห์อัตราซื้อของลูกค้า',
  'Client wishlists': 'รายการที่ลูกค้าสนใจ',
  'Consistent pricing': 'ราคาสม่ำเสมอ',
  'Consolidated reporting': 'รายงานรวมทุกสาขา',
  'Create branded appraisal reports with digital signature in minutes.':
    'สร้างรายงานประเมินติดแบรนด์พร้อมลายเซ็นดิจิทัลในไม่กี่นาที',
  'Different pricing at different branches causes confusion': 'ราคาต่างกันในแต่ละสาขาทำให้สับสน',
  'Dozens of memos out at once — hard to track who has what':
    'เมโมออกเป็นสิบครั้งพร้อมกัน — ตามยากว่าใครถืออะไร',
  'Every stone has a real-time status — available, on hold, on memo, sold.':
    'ทุกเม็ดมีสถานะเรียลไทม์ — พร้อมขาย จองไว้ ออกเมโม ขายแล้ว',
  'GIA certificate verification is manual and slow': 'การตรวจใบรับรอง GIA ทำมือและช้า',
  'GIA-linked appraisal reports with full compliance audit trail.':
    'รายงานประเมินเชื่อม GIA พร้อมบันทึกตรวจสอบครบถ้วน',
  'High-volume memo management and supplier payables at scale.':
    'จัดการเมโมปริมาณสูงและบัญชีเจ้าหนี้ซัพพลายเออร์ในวงกว้าง',
  'Hold & memo tracking': 'ติดตามการจองและเมโม',
  'Immutable record of every appraisal action for regulatory review.':
    'บันทึกที่แก้ไขไม่ได้ของทุกการประเมิน เพื่อการตรวจสอบตามกฎ',
  'Instant inventory search': 'ค้นหาสินค้าทันที',
  'Inter-branch transfers': 'โอนระหว่างสาขา',
  'Inventory is siloed by branch — no unified view': 'สินค้าแยกตามสาขา — ไม่มีภาพรวม',
  'Markup rules ensure every staff member quotes the same price.':
    'กฎมาร์จินทำให้พนักงานทุกคนเสนอราคาเดียวกัน',
  'Move stones between locations with full audit trail.':
    'ย้ายเพชรระหว่างสถานที่พร้อมบันทึกตรวจสอบเต็มรูปแบบ',
  'No audit trail for regulatory compliance': 'ไม่มีบันทึกตรวจสอบเพื่อการกำกับดูแล',
  'No central reporting across the whole business': 'ไม่มีรายงานกลางของทั้งธุรกิจ',
  'No record of what clients have looked at or want': 'ไม่มีบันทึกว่าลูกค้าดูอะไรหรือต้องการอะไร',
  'No visibility into which clients are actually buying': 'มองไม่เห็นว่าลูกค้าคนไหนซื้อจริง',
  'P&L, pipeline, and inventory reports across the entire business.':
    'รายงานกำไรขาดทุน ไปป์ไลน์ และสินค้า ของทั้งธุรกิจ',
  'Pricing is inconsistent across staff': 'ราคาไม่สม่ำเสมอระหว่างพนักงาน',
  'Professional report builder': 'ตัวสร้างรายงานมืออาชีพ',
  'Record what each client wants and notify them when it arrives.':
    'บันทึกสิ่งที่ลูกค้าต้องการและแจ้งเมื่อมาถึง',
  'Role-based access per branch': 'สิทธิ์ตามบทบาทแยกตามสาขา',
  'Search and retrieve any historical appraisal instantly.':
    'ค้นและเรียกประวัติการประเมินย้อนหลังได้ทันที',
  'See Appraiser Features': 'ดูฟีเจอร์สำหรับผู้ประเมิน',
  'See Multi-Location Features': 'ดูฟีเจอร์หลายสาขา',
  'See Retailer Features': 'ดูฟีเจอร์สำหรับร้านปลีก',
  'See Wholesaler Features': 'ดูฟีเจอร์สำหรับผู้ขายส่ง',
  'See all stones across all locations in one dashboard.': 'เห็นทุกเม็ดทุกสถานที่ในแดชบอร์ดเดียว',
  'See which clients convert memos to sales and focus your effort.':
    'เห็นว่าลูกค้าคนไหนแปลงเมโมเป็นยอดขาย แล้วโฟกัสให้ถูก',
  'Showroom-ready inventory management with client-facing tools.':
    'จัดการสินค้าพร้อมโชว์รูม พร้อมเครื่องมือหน้าร้าน',
  'Stones go on hold or memo and get forgotten': 'เพชรถูกจองหรือออกเมโมแล้วถูกลืม',
  'Supplier invoices and payables scattered across emails':
    'ใบแจ้งหนี้ซัพพลายเออร์กระจัดกระจายตามอีเมล',
  'Track all outgoing memos with aging alerts and return deadlines.':
    'ติดตามเมโมที่ออกทั้งหมด พร้อมแจ้งเตือนอายุและกำหนดคืน',
  'Unified inventory and team management across all your branches.':
    'สินค้าและทีมงานรวมทุกสาขาในระบบเดียว',
  'Unified inventory view': 'มุมมองสินค้ารวม',

  // ── About page (v10 coverage) ────────────────────────────────────────
  '14-Day Free Trial': 'ทดลองใช้ฟรี 14 วัน',
  'Full access to all 23 modules': 'เข้าถึงทั้ง 23 โมดูลแบบเต็ม',
  'Automatic activation on first launch': 'เปิดใช้งานอัตโนมัติตั้งแต่เปิดระบบครั้งแรก',
  'Demo countdown shown in the top bar': 'ตัวนับเวลาทดลองใช้แสดงบนแถบบน',
  '365 days of full access with an annual plan': 'ใช้งานเต็มรูปแบบ 365 วันด้วยแพ็กเกจรายปี',
  'Activate from your account settings — no technical setup required':
    'เปิดใช้งานจากหน้าตั้งค่าบัญชี — ไม่ต้องตั้งค่าทางเทคนิค',
  'Renew annually in one click': 'ต่ออายุรายปีในคลิกเดียว',
  'Detailed breakdown of all': 'รายละเอียดครบทุกโมดูลจากทั้งหมด',
  'platform modules and their features': 'โมดูลของแพลตฟอร์มและฟีเจอร์ของแต่ละโมดูล',
  'Built With': 'สร้างด้วย',
  'Total Modules': 'โมดูลทั้งหมด',
  'Diamond merchant management for Thailand': 'ระบบบริหารธุรกิจเพชรสำหรับประเทศไทย',
  // Key Capabilities (emoji renders as a separate node)
  'Full diamond inventory lifecycle management': 'บริหารวงจรชีวิตสินค้าเพชรครบวงจร',
  'Real-time analytics and business intelligence': 'การวิเคราะห์และข่าวกรองธุรกิจแบบเรียลไทม์',
  'Customer & supplier relationship management': 'บริหารความสัมพันธ์ลูกค้าและซัพพลายเออร์',
  'Automated invoice, memo & quotation generation': 'สร้างใบแจ้งหนี้ เมโม และใบเสนอราคาอัตโนมัติ',
  'Role-based access control (Admin, Manager, Sales, Viewer)':
    'ควบคุมสิทธิ์ตามบทบาท (ผู้ดูแล ผู้จัดการ ขาย ผู้ชม)',
  'Regulatory compliance & KYC documentation': 'เอกสารกำกับดูแลและ KYC',
  'Rapaport price calculator with discount tools':
    'เครื่องคำนวณราคา Rapaport พร้อมเครื่องมือส่วนลด',
  'Integrated email communication system': 'ระบบอีเมลสื่อสารในตัว',
  'Customer self-service portal': 'พอร์ทัลบริการตัวเองสำหรับลูกค้า',
  '🌐 Customer self-service portal': '🌐 พอร์ทัลบริการตัวเองสำหรับลูกค้า',
  "Know exactly where every diamond is — and what it's worth right now.":
    'รู้ชัดเจนว่าเพชรทุกเม็ดอยู่ที่ไหน — และมูลค่าปัจจุบันเป็นเท่าไร',
  'Ready to See It in Action?': 'พร้อมดูการใช้งานจริงหรือยัง?',
  'QR code generation & scanning for diamonds': 'สร้างและสแกน QR โค้ดสำหรับเพชร',
  // About page — capability list items
  'CSV/Excel export of inventory data': 'ส่งออกข้อมูลสินค้าเป็น CSV/Excel',
  'Star rating system for customer/supplier quality scoring':
    'ระบบให้คะแนนดาวคุณภาพลูกค้า/ซัพพลายเออร์',
  'Cost basis integration with profit/loss calculations': 'เชื่อมต่อต้นทุนกับการคำนวณกำไรขาดทุน',
  Holds: 'การวางบิล',
  'Upload and store certificate documents (PDF/image)':
    'อัปโหลดและจัดเก็บเอกสารใบรับรอง (PDF/รูปภาพ)',
  'Calculate diamond prices using Rapaport price list with discount/premium adjustments.':
    'คำนวณราคาเพชรจากตารางราคา Rapaport พร้อมปรับส่วนลด/ส่วนเพิ่ม',
  'Glanz CRM — Enterprise Edition ·': 'Glanz CRM — รุ่นองค์กร ·',
  'Glanz CRM — Enterprise Edition': 'Glanz CRM — รุ่นองค์กร',
  // Contact page fragments
  'Questions about': 'สอบถามเกี่ยวกับ',
  '? Our team is based in': '? ทีมงานของเราตั้งอยู่ที่',
  'We respond within 24 hours.': 'เราตอบกลับภายใน 24 ชั่วโมง',
  'Monday – Friday': 'จันทร์ – ศุกร์',
  'Subject *': 'หัวข้อ *',
  'Message *': 'ข้อความ *',
  'Phone / LINE': 'โทรศัพท์ / LINE',
  'I consent to Glanz CRM processing my data in line with the':
    'ข้าพเจ้ายินยอมให้ Glanz CRM ประมวลผลข้อมูลของข้าพเจ้าตาม',
  // Demo page
  '14-day full access. No credit card required.':
    'เข้าถึงครบทุกฟีเจอร์ 14 วัน ไม่ต้องใช้บัตรเครดิต',
  'Sign Up for Free Demo →': 'สมัครเดโมฟรี →',
  'Already have an account?': 'มีบัญชีอยู่แล้ว?',
  'The demo was tailored to our wholesale operation. Within 20 minutes I knew this was the right tool.':
    'เดโมถูกออกแบบมาสำหรับธุรกิจขายส่งของเรา ภายใน 20 นาทีฉันรู้ว่านี่คือเครื่องมือที่ใช่',
  // Security page
  'All traffic is served over HTTPS/TLS 1.2+; plain HTTP requests are redirected and HSTS is enforced.':
    'การรับส่งข้อมูลทั้งหมดผ่าน HTTPS/TLS 1.2+; คำขอ HTTP ทั่วไปจะถูกเปลี่ยนเส้นทางและบังคับใช้ HSTS',
  'Connections to our database (Supabase/PostgreSQL) are TLS-encrypted at all times.':
    'การเชื่อมต่อกับฐานข้อมูล (Supabase/PostgreSQL) ถูกเข้ารหัส TLS ตลอดเวลา',
  'Supabase storage buckets require authenticated requests; public access is disabled for business data.':
    'พื้นที่จัดเก็บ Supabase ต้องการคำขอที่ยืนยันตัวตน; การเข้าถึงสาธารณะถูกปิดใช้งานสำหรับข้อมูลธุรกิจ',
  'All administrative actions are performed through authenticated, permission-checked server endpoints \u2014 database writes are restricted to trusted server code.':
    'การกระทำทั้งหมดดำเนินการผ่านเซิร์ฟเวอร์ที่ยืนยันตัวตนและตรวจสอบสิทธิ์ \u2014 การเขียนฐานข้อมูลจำกัดเฉพาะโค้ดเซิร์ฟเวอร์ที่เชื่อถือได้',
  'Row Level Security (RLS) policies isolate each tenant\u2019s data at the database level.':
    'นโยบาย Row Level Security (RLS) แยกข้อมูลของแต่ละผู้เช่าที่ระดับฐานข้อมูล',
  'Primary data residency in Singapore (ap-southeast-1) via Supabase/AWS.':
    'ที่อยู่ข้อมูลหลักในสิงคโปร์ (ap-southeast-1) ผ่าน Supabase/AWS',
  'Questions about security or data handling? See our':
    'มีคำถามเกี่ยวกับความปลอดภัยหรือการจัดการข้อมูล? ดู',
  'or contact': 'หรือติดต่อ',
  'Security issues can be reported to support@glanzcrm.com — we acknowledge reports within one business day.':
    'ปัญหาด้านความปลอดภัยสามารถรายงานได้ที่ support@glanzcrm.com — เราจะรับรายงานภายใน 1 วันทำการ',
  'Security — Glanz CRM': 'ความปลอดภัย — Glanz CRM',
  // Signup page
  'Get Started with': 'เริ่มต้นกับ',
  'Want to try first?': 'อยากลองก่อน?',
  'Start a free 14-day demo': 'เริ่มเดโมฟรี 14 วัน',
  'glanzcrm.com — Diamond merchant management for Thailand':
    'glanzcrm.com — ระบบบริหารธุรกิจเพชรสำหรับประเทศไทย',
  'Complete Platform Overview —': 'ภาพรวมแพลตฟอร์มทั้งหมด —',
  Modules: 'โมดูล',
  // About page — intro paragraphs
  'is a comprehensive, enterprise-grade Customer Relationship Management platform purpose-built for the diamond and gemstone trading industry. It unifies every aspect of the diamond business — from inventory management and customer relations to financial reporting and regulatory compliance — into a single, powerful platform.':
    'คือแพลตฟอร์ม CRM ระดับองค์กรครบวงจร ที่ออกแบบมาเพื่อธุรกิจค้าเพชรและอัญมณีโดยเฉพาะ รวมทุกด้านของธุรกิจเพชร — ตั้งแต่การจัดการสินค้า ความสัมพันธ์ลูกค้า ไปจนถึงรายงานการเงินและการกำกับดูแล — ไว้ในแพลตฟอร์มเดียวที่ทรงพลัง',
  'Built on modern web technology (Next.js 15, React 19, TypeScript, Supabase), the platform delivers real-time data synchronization, role-based access control, and a rich suite of tools tailored to the unique workflows of diamond traders, wholesalers, and retailers worldwide.':
    'สร้างบนเทคโนโลยีเว็บสมัยใหม่ (Next.js 15, React 19, TypeScript, Supabase) แพลตฟอร์มส่งมอบการซิงก์ข้อมูลแบบเรียลไทม์ การควบคุมสิทธิ์ตามบทบาท และชุดเครื่องมือครบครันที่ออกแบบตาม workflow ของผู้ค้าเพชร ผู้ขายส่ง และผู้ค้าปลีกทั่วโลก',
  '3D diamond viewer & image gallery': 'ตัวดูเพชร 3D และคลังรูปภาพ',
  'New installations start with a free demo period — no license key is required to begin, and all features and modules are fully accessible with no restrictions. Sign up through the website for a 14-day full-access trial.':
    'การติดตั้งใหม่เริ่มต้นด้วยช่วงเดโมฟรี — ไม่ต้องใช้ไลเซンส์คีย์ในการเริ่มต้น และเข้าถึงทุกฟีเจอร์และโมดูลได้ครบถ้วนโดยไม่มีข้อจำกัด สมัครผ่านเว็บไซต์เพื่อรับสิทธิ์ทดลองใช้ฟรี 14 วันแบบเข้าถึงทุกฟังก์ชัน',
  'After your trial, choose a plan to continue. Activate your subscription from your account settings — no technical setup required.':
    'หลังหมดช่วงทดลองใช้ เลือกแพ็กเกจเพื่อใช้งานต่อ ยืนยันการสมัครจากหน้าบัญชีของคุณ — ไม่ต้องตั้งค่าทางเทคนิคใดๆ',
  'After your trial, choose a plan and activate instantly from your account — no technical setup required.':
    'หลังหมดช่วงทดลองใช้ เลือกแพ็กเกจและเปิดใช้งานได้ทันทีจากบัญชีของคุณ — ไม่ต้องตั้งค่าทางเทคนิคใดๆ',
  'Compliance status tracking per customer/supplier':
    'ติดตามสถานะการปฏิบัติตามกฎหมายต่อลูกค้า/ซัพพลายเออร์',
  'Company name, address, and VAT/Tax ID configuration':
    'ตั้งค่าชื่อบริษัท ที่อยู่ และเลขประจำตัวผู้เสียภาษี',
  'Language preference (English / Thai)': 'ตั้งค่าภาษา (อังกฤษ / ไทย)',
  'Profit & loss and financial reporting': 'รายงานกำไรขาดทุนและการเงิน',
  // Module names
  'Diamond Inventory': 'คลังเพชร',
  'CRM (Customer & Supplier)': 'CRM (ลูกค้าและซัพพลายเออร์)',
  'Profit & Loss': 'กำไรและขาดทุน',
  'Quotation Builder': 'ตัวสร้างใบเสนอราคา',
  'Rapaport Calculator': 'เครื่องคำนวณ Rapaport',
  'Email Box': 'กล่องจดหมาย',
  'Inventory Aging': 'อายุสินค้า',
  'Supplier Payables': 'เจ้าหนี้ซัพพลายเออร์',
  'Activity Log': 'บันทึกกิจกรรม',
  // Module descriptions + features
  'Create professional diamond appraisal reports for insurance and valuation purposes.':
    'สร้างรายงานประเมินเพชรอย่างมืออาชีพสำหรับประกันภัยและการประเมินมูลค่า',
  'Complete audit trail of all user actions and system events across the platform.':
    'บันทึกตรวจสอบครบถ้วนของทุกการกระทำของผู้ใช้และเหตุการณ์ระบบทั่วทั้งแพลตฟอร์ม',
  'Deep business intelligence with sales trends, inventory turnover, and customer insights.':
    'ข่าวกรองธุรกิจเชิงลึก แนวโน้มยอดขาย การหมุนเวียนสินค้า และข้อมูลเชิงลึกลูกค้า',
  'Financial P&L reporting with revenue, cost, and margin analysis by period.':
    'รายงานกำไรขาดทุน รายรับ ต้นทุน และมาร์จินแยกตามช่วงเวลา',
  'Integrated email client for managing business communications within the CRM.':
    'อีเมลในตัวสำหรับบริหารการสื่อสารธุรกิจภายใน CRM',
  'Manage consignment memo agreements with clients, including printable memo documents.':
    'บริหารสัญญาเมโมฝากขายกับลูกค้า พร้อมเอกสารเมโมสำหรับพิมพ์',
  'Manage customers and suppliers with star ratings, wishlists, and full contact profiles.':
    'บริหารลูกค้าและซัพพลายเออร์ พร้อมคะแนน รายการสนใจ และโปรไฟล์ครบถ้วน',
  'Manage diamonds received on consignment from suppliers for sale on their behalf.':
    'บริหารเพชรฝากขายจากซัพพลายเออร์เพื่อขายในนามของเขา',
  'Monitor how long diamonds have been in stock to optimize turnover and pricing.':
    'ติดตามว่าเพชรอยู่ในคลังนานแค่ไหน เพื่อเพิ่มการหมุนเวียนและตั้งราคาให้เหมาะ',
  'Platform configuration including company info, branding, and system preferences.':
    'ตั้งค่าแพลตฟอร์ม ข้อมูลบริษัท แบรนด์ และการตั้งค่าระบบ',
  'Record and manage diamond sales transactions with invoice generation.':
    'บันทึกและจัดการรายการขายเพชร พร้อมสร้างใบแจ้งหนี้',
  'Reserve diamonds for clients with time-limited hold management.':
    'จองเพชรให้ลูกค้า พร้อมบริหารระยะเวลาจอง',
  'Role-based access control with full user lifecycle management and audit trails.':
    'ควบคุมสิทธิ์ตามบทบาท บริหารผู้ใช้วงจรเต็ม พร้อมบันทึกตรวจสอบ',
  'Self-service portal for customers to browse available inventory and track their orders.':
    'พอร์ทัลให้ลูกค้าเลือกดูสินค้าและติดตามคำสั่งซื้อด้วยตัวเอง',
  'Side-by-side comparison of multiple diamonds to assist buying and selling decisions.':
    'เปรียบเทียบเพชรหลายเม็ดแบบคู่ขนาน เพื่อการตัดสินใจซื้อขาย',
  'Store and manage GIA, IGI, and other grading certificates linked to diamonds.':
    'จัดเก็บและบริหารใบรับรอง GIA IGI และห้องแล็บอื่น ๆ ที่เชื่อมกับเพชร',
  'Track diamond procurement from suppliers with purchase order documents.':
    'ติดตามการจัดซื้อเพชรจากซัพพลายเออร์พร้อมเอกสารใบสั่งซื้อ',
  'Track outstanding payments to suppliers with aging and payment scheduling.':
    'ติดตามยอดค้างชำระซัพพลายเออร์ พร้อมอายุและกำหนดการชำระ',
  'Track regulatory compliance, KYC records, and conflict-free sourcing documentation.':
    'ติดตามการปฏิบัติตามกฎ KYC และเอกสารแหล่งที่มาปลอดสงคราม',
  'Add, edit, and delete diamond records (shape, carat, color, clarity, cut, price)':
    'เพิ่ม แก้ไข ลบบันทึกเพชร (รูปทรง กะรัต สี ความสะอาด การเจียระไน ราคา)',
  'Advanced filtering by shape, color, clarity, carat range, price range':
    'ตัวกรองขั้นสูงตามรูปทรง สี ความสะอาด ช่วงกะรัต ช่วงราคา',
  'QR code generation and scanner for each stone': 'สร้างและสแกน QR โค้ดสำหรับทุกเม็ด',
  'Image gallery manager with multi-photo upload': 'จัดการคลังรูป อัปโหลดหลายรูปได้',
  '3D diamond viewer for immersive stone preview': 'ตัวดูเพชร 3D สำหรับชมแบบเหมือนจริง',
  'Bulk status management (Available, On Hold, Sold, Memo)':
    'จัดการสถานะเป็นชุด (พร้อมขาย จองไว้ ขายแล้ว เมโม)',
  'Automatic inventory status update on hold creation':
    'อัปเดตสถานะสินค้าอัตโนมัติเมื่อสร้างการจอง',
  'Certificate number search and lookup': 'ค้นหาด้วยเลขใบรับรอง',
  'Certificate expiry and validity tracking': 'ติดตามวันหมดอายุและความถูกต้องของใบรับรอง',
  'Track certificate lab (GIA, IGI, HRD, AGS)': 'ระบุห้องแล็บใบรับรอง (GIA IGI HRD AGS)',
  'Link certificates to diamond inventory records': 'เชื่อมใบรับรองกับบันทึกเพชร',
  'Export certificate registry': 'ส่งออกทะเบียนใบรับรอง',
  'Create, edit, and deactivate user accounts': 'สร้าง แก้ไข และปิดบัญชีผู้ใช้',
  'Role assignment (Admin, Manager, Sales, Viewer)': 'มอบหมายบทบาท (ผู้ดูแล ผู้จัดการ ขาย ผู้ชม)',
  'Permission-based module access control': 'ควบคุมการเข้าถึงโมดูลตามสิทธิ์',
  'Password reset and account security management': 'รีเซ็ตรหัสผ่านและบริหารความปลอดภัยบัญชี',
  'User audit trail per account': 'บันทึกตรวจสอบรายบัญชีผู้ใช้',
  'Active session monitoring': 'ติดตามเซสชันที่ใช้งานอยู่',
  'Customer profiles with contact info, purchase history, and notes':
    'โปรไฟล์ลูกค้า ข้อมูลติดต่อ ประวัติซื้อ และโน้ต',
  'Supplier profiles with payment terms and supply history':
    'โปรไฟล์ซัพพลายเออร์ เงื่อนไขการชำระ และประวัติการจัดส่ง',
  'Search and filter by name, company, country, rating': 'ค้นและกรองตามชื่อ บริษัท ประเทศ คะแนน',
  'Customer wishlist management for preferred diamond specs':
    'บริหารรายการที่ลูกค้าสนใจตามสเปกเพชรที่ต้องการ',
  'Purchase history with cost tracking': 'ประวัติซื้อพร้อมติดตามต้นทุน',
  'Sales history with date, customer, stone, and amount': 'ประวัติขาย วันที่ ลูกค้า เม็ด และยอด',
  'Star ratings for clients and suppliers': 'คะแนนดาวสำหรับลูกค้าและซัพพลายเออร์',
  'Export customer and supplier lists': 'ส่งออกรายชื่อลูกค้าและซัพพลายเออร์',
  'Create and manage sales records linked to inventory':
    'สร้างและจัดการรายการขายที่เชื่อมกับสินค้า',
  'Generate printable sales invoices with company branding':
    'สร้างใบแจ้งหนี้พิมพ์ได้ติดแบรนด์บริษัท',
  'Convert memo to sale with one click': 'แปลงเมโมเป็นยอดขายในคลิกเดียว',
  'Convert quotation to sale': 'แปลงใบเสนอราคาเป็นยอดขาย',
  'Partial payment recording': 'บันทึกการชำระบางส่วน',
  'Track payment status (Paid, Pending, Overdue)': 'ติดตามสถานะชำระ (จ่ายแล้ว รอชำระ เกินกำหนด)',
  'Filter by date range, customer, and payment status': 'กรองตามช่วงวันที่ ลูกค้า และสถานะชำระ',
  'Revenue reporting per sale': 'รายงานรายได้ต่อรายการขาย',
  'Create memo consignment records for diamonds on approval':
    'สร้างบันทึกเมโมฝากขายสำหรับเพชรที่ส่งไปรออนุมัติ',
  'Set memo expiry dates and reminders': 'ตั้งวันหมดอายุเมโมและการแจ้งเตือน',
  'Track memo status (Active, Returned, Converted to Sale)':
    'ติดตามสถานะเมโม (ใช้งาน คืนแล้ว แปลงเป็นขาย)',
  'Link memos to customer profiles': 'เชื่อมเมโมกับโปรไฟล์ลูกค้า',
  'Generate printable memo documents with terms': 'สร้างเอกสารเมโมพิมพ์ได้พร้อมเงื่อนไข',
  'Printable consignment memo documents': 'เอกสารเมโมฝากขายสำหรับพิมพ์',
  'Consignment settlement reports': 'รายงานปิดบัญชีฝากขาย',
  'Link consignment stones to inventory': 'เชื่อมเพชรฝากขายกับสินค้า',
  'Record consignment agreements with suppliers': 'บันทึกสัญญาฝากขายกับซัพพลายเออร์',
  'Track consignment stone status (Available, Sold, Returned)':
    'ติดตามสถานะเพชรฝากขาย (พร้อมขาย ขายแล้ว คืนแล้ว)',
  'Place holds on specific diamonds for customers': 'จองเพชรเฉพาะเม็ดให้ลูกค้า',
  'Set hold expiry dates and durations': 'ตั้งวันหมดอายุและระยะเวลาการจอง',
  'Hold status tracking (Active, Expired, Released)': 'ติดตามสถานะจอง (ใช้งาน หมดอายุ ปล่อยแล้ว)',
  'Release holds and restore availability': 'ปล่อยการจองและคืนสถานะพร้อมขาย',
  'Hold history per customer and per stone': 'ประวัติการจองรายลูกค้าและรายเม็ด',
  'Apply percentage discount or premium to Rap price': 'ใส่ส่วนลดหรือเงินเพิ่มเป็น % บนราคา Rap',
  'Rapaport price lookup by shape, carat, color, clarity':
    'ดูราคา Rapaport ตามรูปทรง กะรัต สี ความสะอาด',
  'Calculate per-carat and total stone price': 'คำนวณราคาต่อกะรัตและราคารวมต่อเม็ด',
  'Apply custom discounts and markups per line': 'ใส่ส่วนลดและมาร์จินเองต่อรายการ',
  'Gross margin calculation per stone and overall': 'คำนวณกำไรขั้นต้นรายเม็ดและรวม',
  'Save calculation history': 'บันทึกประวัติการคำนวณ',
  'Export calculation results': 'ส่งออกผลการคำนวณ',
  'Carat vs. Price scatter plot': 'กราฟจุดกะรัตเทียบราคา',
  'Commission and fee calculation': 'คำนวณคอมมิชชันและค่าธรรมเนียม',
  'Drag-and-drop quotation line items': 'ลากวางรายการในใบเสนอราคา',
  'Build and send professional diamond quotations with custom pricing and branding.':
    'สร้างและส่งใบเสนอราคาเพชรอย่างมืออาชีพ พร้อมราคาและแบรนด์ของคุณ',
  'Company-branded printable quotation PDF': 'PDF ใบเสนอราคาติดแบรนด์บริษัทสำหรับพิมพ์',
  'Save quotation drafts and templates': 'บันทึกฉบับร่างและเทมเพลตใบเสนอราคา',
  'Add stones directly from inventory': 'เพิ่มเม็ดจากคลังโดยตรง',
  'Attach invoices, quotations, and documents': 'แนบใบแจ้งหนี้ ใบเสนอราคา และเอกสาร',
  'Compose and send emails to customers and suppliers': 'เขียนและส่งอีเมลถึงลูกค้าและซัพพลายเออร์',
  'Inbox, Sent, and Draft folder management': 'จัดการโฟลเดอร์ กล่องรับ ส่งแล้ว และฉบับร่าง',
  'Email thread view with full conversation history': 'มุมมองเธรดอีเมลพร้อมประวัติสนทนาเต็ม',
  'Search emails by sender, subject, or content': 'ค้นอีเมลตามผู้ส่ง หัวข้อ หรือเนื้อหา',
  'Link emails to CRM contacts': 'เชื่อมอีเมลกับผู้ติดต่อใน CRM',
  'Aging buckets (0–30, 31–60, 61–90, 90+ days)': 'ช่วงอายุ (0–30, 31–60, 61–90, 90+ วัน)',
  'Visual aging heatmap by stone': 'แผนที่ความร้อนอายุรายเม็ด',
  'Identify slow-moving inventory': 'ระบุสินค้าที่หมุนช้า',
  'Inventory aging and turnover analysis': 'วิเคราะห์อายุและการหมุนเวียนสินค้า',
  'Export aging report': 'ส่งออกรายงานอายุสินค้า',
  'Create purchase orders linked to suppliers': 'สร้างใบสั่งซื้อเชื่อมกับซัพพลายเออร์',
  'Generate printable purchase order documents': 'สร้างเอกสารใบสั่งซื้อสำหรับพิมพ์',
  'Outstanding payables per supplier': 'ยอดค้างชำระรายซัพพลายเออร์',
  'Payables aging report': 'รายงานอายุเจ้าหนี้',
  'Payment due date tracking': 'ติดตามวันครบกำหนดชำระ',
  'Payment history per supplier': 'ประวัติการชำระรายซัพพลายเออร์',
  'Export payables for accounting': 'ส่งออกบัญชีเจ้าหนี้สำหรับบัญชี',
  'Monthly and yearly P&L summaries': 'สรุปกำไรขาดทุนรายเดือนและรายปี',
  'Revenue trend line chart with monthly breakdown': 'กราฟแนวโน้มรายได้แยกรายเดือน',
  'Revenue vs. cost breakdown by period': 'เปรียบเทียบรายรับ-ต้นทุนแยกตามช่วง',
  'Visual P&L charts': 'กราฟกำไรขาดทุนแบบภาพ',
  'Export P&L report to CSV': 'ส่งออกรายงานกำไรขาดทุนเป็น CSV',
  'Revenue vs. cost margin analysis': 'วิเคราะห์มาร์จินรายรับเทียบต้นทุน',
  'Sales performance charts by period, shape, and customer':
    'กราฟผลงานขายตามช่วงเวลา รูปทรง และลูกค้า',
  'Diamond shape distribution bar chart': 'กราฟแท่งการกระจายรูปทรงเพชร',
  'Inventory status pie chart': 'แผนภูมิวงกลมสถานะสินค้า',
  'Top customers and suppliers by volume': 'ลูกค้าและซัพพลายเออร์สูงสุดตามปริมาณ',
  'Diamond shape and quality distribution reports': 'รายงานการกระจายรูปทรงและคุณภาพเพชร',
  'Price per carat comparison': 'เปรียบเทียบราคาต่อกะรัต',
  'Exportable analytics data': 'ข้อมูลวิเคราะห์ส่งออกได้',
  'Chronological log of all create, update, delete actions':
    'บันทึกลำดับเวลาของทุกการสร้าง แก้ไข ลบ',
  'Filter by user, module, action type, and date range':
    'กรองตามผู้ใช้ โมดูล ประเภทการกระทำ และช่วงวันที่',
  'Real-time log updates': 'อัปเดตบันทึกแบบเรียลไทม์',
  'Searchable log entries': 'ค้นหารายการบันทึกได้',
  'IP address and session tracking per action': 'บันทึก IP และเซสชันทุกการกระทำ',
  'Export activity log for compliance reporting': 'ส่งออกบันทึกกิจกรรมเพื่อรายงานการกำกับดูแล',
  'Record appraised value, date, and appraiser details': 'บันทึกมูลค่าประเมิน วันที่ และผู้ประเมิน',
  'Link appraisals to inventory stones': 'เชื่อมรายงานประเมินกับเพชรในคลัง',
  'Appraisal history per diamond': 'ประวัติการประเมินรายเม็ด',
  'Generate formal appraisal documents': 'สร้างเอกสารประเมินอย่างเป็นทางการ',
  'Printable appraisal certificates': 'ใบรับรองการประเมินสำหรับพิมพ์',
  'Export comparison as report': 'ส่งออกผลเปรียบเทียบเป็นรายงาน',
  'Compare up to 4 diamonds simultaneously': 'เปรียบเทียบเพชรได้ถึง 4 เม็ดพร้อมกัน',
  'Side-by-side attribute comparison (carat, color, clarity, cut, price)':
    'เปรียบเทียบคุณสมบัติแบบคู่ขนาน (กะรัต สี ความสะอาด การเจียระไน ราคา)',
  'Compare multiple stones side by side': 'เปรียบเทียบหลายเม็ดแบบคู่ขนาน',
  'KYC (Know Your Customer) record management': 'บริหารบันทึก KYC (รู้จักลูกค้าของคุณ)',
  'Document upload for compliance evidence': 'อัปโหลดเอกสารหลักฐานการกำกับดูแล',
  'Conflict-free diamond sourcing documentation': 'เอกสารแหล่งที่มาเพชรปลอดสงคราม',
  'Regulatory reporting exports': 'ส่งออกรายงานตามกฎระเบียบ',
  'Track order and memo status': 'ติดตามสถานะคำสั่งซื้อและเมโม',
  'View available stones with specs and pricing': 'ดูเพชรพร้อมขายพร้อมสเปกและราคา',
  'Secure customer login': 'ระบบล็อกอินลูกค้าที่ปลอดภัย',
  'Download invoices and certificates': 'ดาวน์โหลดใบแจ้งหนี้และใบรับรอง',
  'Wishlist management from portal': 'บริหารรายการสนใจจากพอร์ทัล',
  'Track payment and delivery status': 'ติดตามสถานะการชำระและการจัดส่ง',
  'Multi-currency support': 'รองรับหลายสกุลเงิน',
  'Real-time Supabase data subscriptions': 'ดึงข้อมูล Supabase แบบเรียลไทม์',
  'Notification and alert preferences': 'การตั้งค่าการแจ้งเตือน',
  'Company info used across all printable documents': 'ข้อมูลบริษัทใช้ในเอกสารพิมพ์ทุกฉบับ',
  'App name and branding customization': 'ปรับแต่งชื่อแอปและแบรนด์',
  'Live KPI cards (Total Inventory, Active Holds, Pending Memos, Monthly Revenue)':
    'การ์ด KPI สด (สินค้ารวม การจองที่ใช้งาน เมโมรอ รายได้รายเดือน)',
  'Filter by date range and category': 'กรองตามช่วงวันที่และหมวดหมู่',
  'Filter by shape, color, clarity, and age range': 'กรองตามรูปทรง สี ความสะอาด และช่วงอายุ',
  'Filter by supplier, date, and status': 'กรองตามซัพพลายเออร์ วันที่ และสถานะ',
  'Aging alerts for stones over threshold': 'แจ้งเตือนเมื่อสินค้าเกินเกณฑ์อายุ',
  'Track replacement value vs. market value': 'เทียบมูลค่าทดแทนกับมูลค่าตลาด',
  'Visual quality grade indicators': 'ตัวชี้วัดเกรดคุณภาพแบบภาพ',
  'Compliance audit trail': 'บันทึกตรวจสอบการกำกับดูแล',
  'Customer-facing diamond browsing interface': 'หน้าเลือกดูเพชรสำหรับลูกค้า',

  // ── Pricing page ─────────────────────────────────────────────────────
  'Plans for Every Diamond Business': 'แพ็กเกจสำหรับธุรกิจเพชรทุกขนาด',
  'No hidden fees. No long-term contracts. 14-day free trial on every plan.':
    'ไม่มีค่าธรรมเนียมแอบแฝง ไม่มีสัญญาระยะยาว ทดลองใช้ฟรี 14 วันทุกแพ็กเกจ',
  Annual: 'รายปี',
  '★ Most Popular': '★ ยอดนิยม',
  '/year': '/ ปี',
  'For solo traders and small shops': 'สำหรับนักค้าอิสระและร้านเล็ก',
  'For growing teams and active traders': 'สำหรับทีมที่กำลังเติบโตและนักค้าตัวยง',
  'For large operations and multi-location': 'สำหรับธุรกิจขนาดใหญ่และหลายสาขา',
  '500 inventory items': 'สินค้า 500 รายการ',
  '5,000 inventory items': 'สินค้า 5,000 รายการ',
  '1 user (Owner role)': 'ผู้ใช้ 1 คน (บทบาทเจ้าของ)',
  'Up to 3 users (all roles)': 'ผู้ใช้ได้ถึง 3 คน (ทุกบทบาท)',
  'Up to 10 users': 'ผู้ใช้ได้ถึง 10 คน',
  'Rapaport price calculator': 'เครื่องคำนวณราคา Rapaport',
  'Basic P&L reporting': 'รายงานกำไรขาดทุนพื้นฐาน',
  'Email support': 'ซัพพอร์ททางอีเมล',
  '14-day free trial': 'ทดลองใช้ฟรี 14 วัน',
  'Full CRM + deal pipeline': 'CRM เต็มรูปแบบ + ไปป์ไลน์ดีล',
  'Advanced analytics': 'การวิเคราะห์ขั้นสูง',
  'Memo & hold management': 'จัดการเมโมและการจอง',
  'Appraisal module': 'โมดูลประเมินราคา',
  'Priority support': 'ซัพพอร์ทแบบพิเศษ',
  'Unlimited inventory': 'สินค้าไม่จำกัด',
  'Multi-location support': 'รองรับหลายสาขา',
  'API access': 'การเข้าถึง API',
  'Dedicated account manager': 'ผู้จัดการบัญชีประจำ',
  'Custom onboarding': 'ออนบอร์ดดิ้งแบบเฉพาะบุคคล',
  'SLA guarantee': 'รับประกันระดับบริการ (SLA)',
  'White-label option': 'ติดแบรนด์ของคุณ (White-label)',
  'Add-Ons': 'ส่วนเสริม',
  'Extra Users': 'ผู้ใช้เพิ่ม',
  '$49/user/month': '$49/ผู้ใช้/เดือน',
  'Add more team members to any plan.': 'เพิ่มสมาชิกทีมในแพ็กเกจใดก็ได้',
  'Extra Inventory': 'สินค้าเพิ่ม',
  '$29/1,000 items': '$29/1,000 รายการ',
  'Scale your inventory beyond plan limits.': 'ขยายสินค้าเกินขีดจำกัดของแพ็กเกจ',
  'Custom Integrations': 'การเชื่อมต่อแบบเฉพาะ',
  'From $199/month': 'เริ่มต้น $199/เดือน',
  'Connect to your accounting or e-commerce platform.':
    'เชื่อมต่อกับระบบบัญชีหรือแพลตฟอร์มอีคอมเมิร์ซของคุณ',
  'Inventory items': 'จำนวนสินค้า',
  'Rapaport calculator': 'เครื่องคำนวณ Rapaport',
  'QR scanning': 'สแกน QR',
  'GIA verification': 'ตรวจสอบ GIA',
  'Full CRM': 'CRM เต็มรูปแบบ',
  'Deal pipeline': 'ไปป์ไลน์ดีล',
  'Multi-location': 'หลายสาขา',
  'Dedicated manager': 'ผู้จัดการประจำ',
  'White-label': 'ติดแบรนด์ของคุณ',
  'Frequently Asked Questions': 'คำถามที่พบบ่อย',
  'Can I switch plans later?': 'เปลี่ยนแพ็กเกจภายหลังได้ไหม?',
  'Yes. Upgrade or downgrade at any time. Changes take effect on your next billing cycle.':
    'ได้ อัปเกรดหรือลดแพ็กเกจได้ทุกเมื่อ การเปลี่ยนแปลงมีผลในรอบการเรียกเก็บเงินถัดไป',
  'Is there a free trial?': 'มีทดลองใช้ฟรีไหม?',
  'All plans include a 14-day free trial. No credit card required to start.':
    'ทุกแพ็กเกจรวมทดลองใช้ฟรี 14 วัน ไม่ต้องใช้บัตรเครดิตในการเริ่ม',
  'Do you support Thai Baht (THB)?': 'รองรับเงินบาทไทย (THB) ไหม?',
  'Yes. Glanz CRM supports both THB and USD with a live currency toggle.':
    'รองรับทั้ง THB และ USD พร้อมสลับสกุลเงินได้ทันที',
  'What happens to my data if I cancel?': 'ถ้ายกเลิก ข้อมูลของฉันจะเป็นอย่างไร?',
  'You can export all your data at any time. We retain it for 30 days after cancellation.':
    'คุณส่งออกข้อมูลทั้งหมดได้ทุกเมื่อ เราเก็บข้อมูลไว้ 30 วันหลังยกเลิก',
  'Start Your 14-Day Free Trial': 'เริ่มทดลองใช้ฟรี 14 วัน',
  'No credit card required. Full access to all Professional features.':
    'ไม่ต้องใช้บัตรเครดิต เข้าถึงฟีเจอร์ Professional ทั้งหมด',
  'Book a Demo & Start Trial': 'นัดดูเดโมและเริ่มทดลองใช้',
  Company: 'บริษัท',
  Resources: 'แหล่งข้อมูล',
  Legal: 'ข้อกฎหมาย',
  About: 'เกี่ยวกับเรา',
  Security: 'ความปลอดภัย',
  'Help & Support': 'ช่วยเหลือและสนับสนุน',
  '. All rights reserved.': '. สงวนลิขสิทธิ์',
};

/**
 * Pattern-based translations for dynamic strings exact-match can't catch
 * (e.g. "12d in stock", "+18%margin"). Each entry: [pattern, replace].
 */
const DYNAMIC_PATTERNS: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  // Combined "Vault A · 12d in stock" (prefix kept as-is, already translated or a proper noun)
  [/^([\w\s\u0E00-\u0E7F]+?)\s*·\s*(\d+)d in stock$/u, (m) => `${m[1]} · อยู่ในสต็อก ${m[2]} วัน`],
  [
    /^(?:·\s*)?(\d+)d in stock$/,
    (m) => `${m[0].startsWith('·') ? '· ' : ''}อยู่ในสต็อก ${m[1]} วัน`,
  ],
  [/^\+(\d+)\s*%\s*margin$/, (m) => `+${m[1]}% อัตรากำไร`],
  [/^Vault ([A-E])$/, (m) => `ตู้เก็บ ${m[1]}`],
  [/^(\d+) in stock$/, (m) => `อยู่ในสต็อก ${m[1]} ชิ้น`],
  [/^Save \$([\d,]+) vs monthly$/, (m) => `ประหยัด $${m[1]} เมื่อเทียบรายเดือน`],
  // "<City>, Thailand" from contact settings (city is data, keep as-is)
  [/^([\w\s.'-]+),\s*Thailand$/u, (m) => `${m[1]}, ประเทศไทย`],
  // "$22,800 · 3 weeks ago" style timestamps from mock activity rows
  [
    /^(\$[\d,]+)\s*·\s*(\d+)\s*(second|minute|hour|day|week|month)s?\s+ago$/i,
    (m) => `${m[1]} · ${m[2]} ${AGO_TH[m[3].toLowerCase()] ?? m[3]}ที่ผ่านมา`,
  ],
  [/^across\s*([\d,]+)\+?\s*merchants$/u, (m) => `จากพ่อค้ากว่า ${m[1]} ราย`],
  [/^Step\s*(\d+)\s*of\s*(\d+)$/iu, (m) => `ขั้นตอน ${m[1]} จาก ${m[2]}`],
  // Diamond shapes + carat in mock rows: "Round 1.52ct" → "เจียระไนกลม 1.52 กะรัต"
  [
    /^(Round|Princess|Oval|Cushion|Emerald|Pear|Marquise|Radiant|Asscher|Heart)\s+([\d.]+)ct$/u,
    (m) => `${SHAPE_TH[m[1]] ?? m[1]} ${m[2]} กะรัต`,
  ],
  // Pricing-widget mock values: "$5,200 / ct", "Round F-VS1 → +18%", "Wholesale (−5%)"
  [/^\$([\d,]+)\s*\/\s*ct$/, (m) => `$${m[1]} / กะรัต`],
  [
    /^(Round|Princess|Oval|Cushion|Emerald|Pear|Marquise|Radiant|Asscher|Heart)\s+([A-Z]-\S+)\s*→\s*\+(\d+)%$/u,
    (m) => `${SHAPE_TH[m[1]] ?? m[1]} ${m[2]} → +${m[3]}%`,
  ],
  [/^Wholesale\s*\(([−-]\d+)%\)$/, (m) => `ขายส่ง (${m[1]}%)`],
  [/^Retail\s*\(\+(\d+)%\)$/, (m) => `ขายปลีก (+${m[1]}%)`],
  // Memo mock rows: "3 items · $31,400", "Today, 10:32 AM", "Yesterday, 4:15 PM"
  [/^(\d+)\s+items?\s*·\s*(\$[\d,]+)$/, (m) => `${m[1]} รายการ · ${m[2]}`],
  [/^Today,\s*(\d{1,2}:\d{2})\s*(AM|PM)$/i, (m) => `วันนี้ ${m[1]} น.`],
  [/^Yesterday,\s*(\d{1,2}:\d{2})\s*(AM|PM)$/i, (m) => `เมื่อวาน ${m[1]} น.`],
  // "Glanz CRM · <Step Label>" tour footer — label part already translatable.
  // Tail must be Thai-free so already-translated output can't re-match (loop guard).
  [/^Glanz CRM\s*·\s*([^\u0E00-\u0E7F]+)$/, (m) => `Glanz CRM · ${EN_TH[m[1].trim()] ?? m[1]}`],
];

const AGO_TH: Record<string, string> = {
  second: 'วินาที',
  minute: 'นาที',
  hour: 'ชั่วโมง',
  day: 'วัน',
  week: 'สัปดาห์',
  month: 'เดือน',
};

const SHAPE_TH: Record<string, string> = {
  Round: 'เจียระไนกลม',
  Princess: 'พรินเซส',
  Oval: 'รี',
  Cushion: 'คุชชัน',
  Emerald: 'อีเมรัลด์',
  Pear: 'หยดน้ำ',
  Marquise: 'มาร์คีส',
  Radiant: 'เรเดียนต์',
  Asscher: 'แอชเชอร์',
  Heart: 'หัวใจ',
};

const TH_MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];
const EN_MONTHS: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
};

/** "Sep 15, 2026" → "15 ก.ย. 2569" (Buddhist era), applied standalone or inside longer text. */
function translateDates(text: string): string {
  return text.replace(
    /\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(\d{1,2}),\s*(\d{4})\b/g,
    (_all, mon: string, day: string, year: string) =>
      `${parseInt(day, 10)} ${TH_MONTHS[EN_MONTHS[mon]]} ${parseInt(year, 10) + 543}`
  );
}

function translateText(raw: string): string | null {
  const text = raw.trim();
  if (!text || !/[\x20-\x7E]/.test(text)) return null;
  const exact = EN_TH[text];
  if (exact && exact !== text) return raw.replace(text, exact);
  // Quoted wrapper: some pages render copy inside literal quote marks
  // ("Like this."), which breaks exact-match against unquoted keys. Strip
  // the wrapper, look up the inner text, re-apply the same wrapper.
  const stripped = text.replace(/^["“”'']+|["“”'']+$/g, '');
  if (stripped && stripped !== text) {
    const inner = EN_TH[stripped];
    if (inner) {
      const open = text[0];
      return raw.replace(text, open + inner + (text.length > 1 ? text[text.length - 1] : ''));
    }
  }
  for (const [re, fn] of DYNAMIC_PATTERNS) {
    const m = text.match(re);
    if (m) return raw.replace(text, fn(m));
  }
  // Date-only nodes ("Sep 15, 2026" or "September 15, 2026") — common on unwired pages.
  if (
    /^(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2},\s*\d{4}$/.test(
      text
    )
  ) {
    return raw.replace(text, translateDates(text));
  }
  return null;
}

function readStoredLang(): LangCurrencyContextType['lang'] {
  if (typeof window === 'undefined') return 'EN';
  try {
    return window.localStorage.getItem('glanz_lang') === 'TH' ? 'TH' : 'EN';
  } catch {
    return 'EN';
  }
}

/** Reverse map: English UI string → Thai, built once from DICT + CHROME_PHRASES. */
const EN_TH: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const key of Object.keys(DICT)) {
    const { en, th } = DICT[key];
    // Later entries win; keep the map deterministic.
    if (en && th) map[en] = th;
  }
  Object.assign(map, CHROME_PHRASES);
  return map;
})();

/** Original (English) content of every node/placeholder we mutate, so the
 * TH→EN toggle can restore exactly what React will not rewrite. */
const ORIGINAL_TEXT = new WeakMap<Text, string>();
const ORIGINAL_PLACEHOLDER = new WeakMap<Element, string>();
/** Original aria-label per element, for the same TH→EN restore reason. */
const ORIGINAL_ARIA = new WeakMap<Element, string>();

export default function AutoTranslate() {
  const ctx = useContext(LangCurrencyContext as React.Context<LangCurrencyContextType | null>);
  // Standalone pages (super-admin login) render outside the provider — they
  // still get TH coverage by reading the persisted preference directly.
  const lang = ctx?.lang ?? readStoredLang();

  useEffect(() => {
    // Keep the document language in sync (WCAG 3.1.1) so screen readers,
    // browser translation heuristics, and crawlers see the active language.
    document.documentElement.lang = lang === 'TH' ? 'th' : 'en';
    if (lang !== 'TH') {
      // TH→EN restore: React skips rewriting DOM we mutated (vdom strings are
      // unchanged from its point of view), so put the captured originals back.
      for (const el of Array.from(
        document.body.querySelectorAll('input[placeholder], textarea[placeholder]')
      )) {
        const orig = ORIGINAL_PLACEHOLDER.get(el);
        if (orig !== undefined) el.setAttribute('placeholder', orig);
      }
      for (const el of Array.from(document.body.querySelectorAll('[aria-label]'))) {
        const orig = ORIGINAL_ARIA.get(el);
        if (orig !== undefined) el.setAttribute('aria-label', orig);
      }
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let node: Text | null;
      while ((node = walker.nextNode() as Text | null)) {
        const orig = ORIGINAL_TEXT.get(node);
        if (orig !== undefined) node.nodeValue = orig;
      }
      return;
    }

    const walk = (root: Element) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node: Text | null;
      while ((node = walker.nextNode() as Text | null)) {
        const raw = node.nodeValue ?? '';
        const th = translateText(raw);
        if (th && th !== raw) {
          if (!ORIGINAL_TEXT.has(node)) ORIGINAL_TEXT.set(node, raw);
          node.nodeValue = th;
        }
      }
    };

    const scanPlaceholders = (root: ParentNode) => {
      root.querySelectorAll('input[placeholder], textarea[placeholder]').forEach((el) => {
        const p = el.getAttribute('placeholder') ?? '';
        const th = EN_TH[p];
        if (th && th !== p) {
          if (!ORIGINAL_PLACEHOLDER.has(el)) ORIGINAL_PLACEHOLDER.set(el, p);
          el.setAttribute('placeholder', th);
        }
      });
      // Screen-reader labels are user-facing copy too (icon-only buttons, the
      // newsletter field) — translate them with the same dictionary so the TH
      // locale is fully Thai for assistive tech as well as sighted users.
      root.querySelectorAll('[aria-label]').forEach((el) => {
        const p = el.getAttribute('aria-label') ?? '';
        const th = EN_TH[p];
        if (th && th !== p) {
          if (!ORIGINAL_ARIA.has(el)) ORIGINAL_ARIA.set(el, p);
          el.setAttribute('aria-label', th);
        }
      });
    };

    const run = () => {
      walk(document.body);
      scanPlaceholders(document.body);
    };

    // Initial pass after paint.
    const raf = requestAnimationFrame(run);

    // Hydration catch-up: React replaces server-rendered text nodes while it
    // hydrates, and those replacements can land after the first paint. Re-walk
    // shortly after load so nothing hydrated in English survives. Idempotent —
    // already-translated (Thai) nodes fail the ASCII guard and are skipped.
    const catchUp = () => run();
    window.addEventListener('load', catchUp, { once: true });
    const t1 = window.setTimeout(catchUp, 400);
    const t2 = window.setTimeout(catchUp, 1500);

    // Catch re-renders/tab switches/data loads. characterData covers React
    // updating an existing text node in place (its write bypasses childList).
    const observer = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'characterData') {
          const node = m.target as Text;
          const cur = node.nodeValue ?? '';
          const th = translateText(cur);
          // th === cur would re-fire this exact mutation forever (observer
          // loop) — only ever write when the value actually changes.
          if (th && th !== cur) {
            // React rewrote this node with fresh English — replace any stale
            // captured original so TH→EN restore puts back the right text.
            ORIGINAL_TEXT.set(node, cur);
            node.nodeValue = th;
          }
          continue;
        }
        for (const n of Array.from(m.addedNodes)) {
          if (n.nodeType === Node.ELEMENT_NODE) {
            walk(n as Element);
            scanPlaceholders(n as ParentNode);
          } else if (n.nodeType === Node.TEXT_NODE) {
            const t = (n as Text).nodeValue ?? '';
            const th = translateText(t);
            if (th && th !== t) {
              if (!ORIGINAL_TEXT.has(n as Text)) ORIGINAL_TEXT.set(n as Text, t);
              (n as Text).nodeValue = th;
            }
          }
        }
        if (m.type === 'attributes' && m.attributeName === 'placeholder') {
          const el = m.target as HTMLElement;
          const p = el.getAttribute('placeholder') ?? '';
          const th = EN_TH[p];
          // Guard against no-op writes: setting an attribute to its current
          // value still fires a mutation record, which would loop forever.
          if (th && th !== p) {
            if (!ORIGINAL_PLACEHOLDER.has(el)) ORIGINAL_PLACEHOLDER.set(el, p);
            el.setAttribute('placeholder', th);
          }
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['placeholder'],
      characterData: true,
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('load', catchUp);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      observer.disconnect();
    };
  }, [lang]);

  return null;
}
